import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Local storage key for language persistence
const STORAGE_KEY = 'lang';

const savedLang = ((): 'en' | 'th' | 'fil' => {
  try {
    const v = localStorage.getItem(STORAGE_KEY) as 'en' | 'th' | 'fil' | null;
    if (v === 'th') return 'th';
    if (v === 'fil') return 'fil';
    return 'en';
  } catch {
    return 'en';
  }
})();

// Minimal bootstrap resources; full dictionaries live in separate files and are merged here
const resources = {
  en: {
    theme: {
      dark: 'Dark',
      light: 'Light',
      blue: 'Blue',
      wood: 'Wood'
    },
    home: {
      quickActions: 'Quick Actions',
      recentActivity: 'Recent Activity',
      fleetStatusOverview: 'Fleet Status Overview',
      welcomeTitle: 'Welcome to OurScooters',
      welcomeSubtitle: 'Your comprehensive scooter fleet management dashboard',
      addScooter: 'New Scooter',
      registerScooter: 'Register new scooter',
      addClient: 'New Client',
      registerClient: 'Register new customer',
      newContract: 'New Contract',
      createAgreement: 'Create rental agreement',
      org: 'Org',
      allOrgs: 'All orgs',
      availableScooters: 'Available Scooters',
      readyForRental: 'Ready for rental',
      rentedScooters: 'Rented Scooters',
      currentlyInUse: 'Currently in use',
      maintenance: 'Maintenance',
      underRepair: 'Under repair',
      fleetOverview: 'Fleet Overview',
      available: 'available',
      rented: 'rented',
      activeRevenue: 'Active Revenue',
      monthlyRecurring: 'Monthly recurring revenue',
      totalClients: 'Total Clients',
      activeContracts: 'active contracts',
      avgBattery: 'Avg Battery',
      fleetBatteryHealth: 'Fleet battery health',
      avgFuel: 'Avg Fuel',
      fuel: 'fuel',
      electric: 'electric',
      fleetROI: 'Fleet ROI',
      rev: 'rev',
      cost: 'cost',
      inMaintenance: 'sa maintenance',
      newClientRegistered: 'Bagong client',
      registered: 'narehistro'
    },
    users: {
      title: 'Mga User',
      errorLoading: 'Nabigo ang pag-load ng users.',
      id: 'ID',
      email: 'Email',
      role: 'Role',
      username: 'Username',
      org: 'Org',
      roleSuperadmin: 'Superadmin',
      roleAdmin: 'Admin',
      roleStaff: 'Staff'
    },
    management: {
      title: 'Organization Management',
      errorLoading: 'Error loading settings.',
      timeFormat: 'Time Format',
      defaultCurrency: 'Default Currency',
      defaultCountryCode: 'Default Country Code',
      defaultDailyPrice: 'Default Daily Price',
      defaultDeposit: 'Default Contract Deposit',
      defaultRentLength: 'Default Rent Length (Days)',
      defaultPaymentMethod: 'Default Payment Method',
      buttonStyle: 'Button Style',
      buttonStyleDesc: 'Choose the visual style for all buttons throughout the application.',
      countryCodeHint: '1 for USA, 44 for UK, etc.',
      countryCodeExplain: 'Digits only. Will prefix phone numbers in client forms.',
      saving: 'Saving...',
      saveSettings: 'Save Settings',
      saved: 'Saved',
      modernStyle: 'Modern Style',
      classicStyle: 'Classic Style',
      originalStyle: 'Original Style',
      btnPrimary: 'Primary',
      btnSecondary: 'Secondary',
      btnGhost: 'Ghost',
      btnDanger: 'Danger',
      modernDesc: 'Gradient backgrounds with micro-interactions and rounded corners',
      classicDesc: 'Clean, minimal design with theme-aware colors and subtle borders',
      originalDesc: 'The original button design you liked before refactoring',
      timeFormatExample1: '15 September 2025 6:09 PM',
      timeFormatExample2: 'September 15 2025 6:09 PM',
      timeFormatExample3: '15 September 2025 18:09',
      timeFormatExample4: 'September 15 2025 18:09'
    },
    common: {
      language: 'Language',
      theme: 'Theme',
      save: 'Save',
      cancel: 'Cancel',
      logout: 'Logout',
      loading: 'Loading...',
      reset: 'Reset',
      close: 'Close',
      back: 'Back',
      delete: 'Delete',
      clearAll: 'Clear All',
      upload: 'Upload',
      uploading: 'Uploading...',
      selectPhoto: 'Select Photo',
      addNotes: 'Add notes...',
      notes: 'Notes',
      optional: 'Optional',
      retake: 'Retake',
      usePhoto: 'Use Photo',
      viewAll: 'View All Photos',
      confirmDelete: 'Are you sure you want to delete this photo?',
      add: 'Add',
      copied: 'Copied',
      expand: 'Expand',
      collapse: 'Collapse',
      updateFailed: 'Update failed',
      updateFailedWithMessage: 'Update failed: {{message}}',
      failedToSendEmail: 'Failed to send email',
      statusChangeNotAllowed: 'This status change is not allowed',
      expenseAddedSuccessfully: 'Expense added successfully',
      edit: 'Edit'
    },
    toasts: {
      contractCreated: 'Contract created successfully',
      contractUpdated: 'Contract updated successfully',
      contractDeleted: 'Contract deleted successfully',
      contractCreateFailed: 'Failed to create contract',
      contractUpdateFailed: 'Failed to update contract',
      contractDeleteFailed: 'Failed to delete contract',
      scooterCreated: 'Scooter created successfully',
      scooterUpdated: 'Scooter updated successfully',
      scooterDeleted: 'Scooter deleted successfully',
      scooterStatusUpdated: 'Scooter status updated successfully',
      scooterStatusUpdateFailed: 'Failed to update scooter status',
      clientCreated: 'Client created successfully',
      clientUpdated: 'Client updated successfully',
      clientDeleted: 'Client deleted successfully',
      settingsUpdated: 'Settings updated successfully!',
      scooterStatusChangedAuto: 'Scooter Status Changed Automatically',
      scooterStatusChangeDesc: '{{scooterName}} was moved from {{fromStatus}} to {{toStatus}} {{reason}}.',
      becauseContractActivated: 'because the contract was activated',
      becauseContractDeactivated: 'because the contract was closed/archived',
      automatically: 'automatically',
      viewScooter: 'View Scooter',
      scooterOpenedFromNotif: 'Opened {{scooterName}} details',
      scooterOpenedFromNotifDesc: 'This scooter was automatically opened from a status change notification.',
      closedContractsMoveRestriction: 'Closed contracts can only be moved to Archived.'
    },
    login: {
      title: 'Login',
      email: 'Email',
      username: 'Username',
      password: 'Password',
      signIn: 'Sign in',
      signingIn: 'Signing in...',
      login: 'Login',
      loggingIn: 'Logging in...',
      loginFailed: 'Login failed',
      language: 'Language'
    },
    sidebar: {
      scooters: 'Scooters',
      clients: 'Clients',
      contracts: 'Contracts',
      finance: 'Finance',
      users: 'Users',
      activity: 'Activity Log',
      management: 'Management',
      howItWorks: 'How It Works',
      signIn: 'Sign in',
      expand: 'Expand sidebar',
      collapse: 'Collapse sidebar',
      logoutConfirm: 'Are you sure you want to logout?'
    },
    toolbar: {
      searchPlaceholder: 'Search by ID, model, or status…',
      clear: 'Clear',
      listView: 'List view',
      kanbanView: 'Kanban view',
      calendarView: 'Calendar',
      gridView: 'Grid view',
      mapView: 'Map view',
      filter: 'Filter',
      export: 'Export',
      total: 'Total: {{count}}',
      queryLabel: 'query',
      clearQuery: 'Clear query',
      removeFilter: 'Remove {{filter}}'
    },
    // Generic UI labels
    ui: {
      zoom: 'Zoom'
    },
    calendar: {
      week: 'Week',
      month: 'Month',
      all: 'All',
      plusMore: '+{{count}} more',
      scrollHint: 'Shift+Scroll to move horizontally',
      hideEmpty: 'Hide scooters without upcoming contracts'
    },
    status: {
      available: 'Available',
      rented: 'Rented',
      maintenance: 'Maintenance',
      retired: 'Retired',
      draft: 'Draft',
      active: 'Active',
      closed: 'Closed',
      archived: 'Archived'
    },
    clients: {
      id: 'Client ID',
      name: 'Name',
      messenger: 'WhatsApp',
      newClient: 'New client',
      creating: 'Creating client...',
      fullName: 'Full Name',
      fullNamePlaceholder: 'Enter full name',
      email: 'Email',
      emailPlaceholder: 'Enter email address',
      localPhone: 'Local Phone Number',
      localPhonePlaceholder: 'Enter local phone number (optional)',
      messengerNumber: 'WhatsApp Number',
      countryCodePlaceholder: '1',
      messengerNumberPlaceholder: 'Enter WhatsApp number (optional)',
      lineId: 'LINE ID',
      lineIdPlaceholder: 'Enter LINE ID (optional)',
      whatsappButton: 'WhatsApp',
      lineButton: 'LINE',
      contactViaWhatsApp: 'Contact via WhatsApp',
      contactViaLINE: 'Contact via LINE',
      notes: 'Notes',
      notesPlaceholder: 'Additional notes about this client (e.g., different country phone number)',
      addClient: 'New client',
      detailsTitle: 'Client Details',
      created: 'Created',
      clientId: 'Client ID',
      edit: 'Edit Client',
      previousContracts: 'Previous contracts:',
      noCurrentContracts: 'No current contracts',
      editClient: 'Edit Client',
      newContractBtn: 'New Contract',
      close: 'Close',
      statusHistory: 'Status history',
      unknownClient: 'Unknown Client'
    },
    scooters: {
      id: 'Scooter ID',
      modelRequired: 'Model is required',
      batteryRange: 'Battery must be 0–100',
      odometerNegative: 'Odometer cannot be negative',
      fuelRange: 'Fuel % must be 0–100',
      incompatibleEnergy: 'Electric cannot have fuel; fuel vehicles cannot have battery',
      editScooter: 'Edit Scooter',
      newScooter: 'New scooter',
      updating: 'Updating scooter...',
      creating: 'Creating scooter...',
      model: 'Model',
      modelPlaceholder: 'e.g. Ninebot Max',
      status: 'Status',
      typeLevel: 'Type & Level',
      odometer: 'Odometer',
      qrCode: 'QR Code',
      qrPlaceholder: 'Optional QR code',
      plateNumber: 'Plate Number',
      platePlaceholder: 'Optional plate number',
      nickname: 'Nickname',
      nicknamePlaceholder: 'e.g. Red Honda #1',
      nicknameOptional: 'Nickname (optional)',
      brand: 'Brand',
      brandPlaceholder: 'Optional brand',
      vehicleType: 'Vehicle type',
      electric: 'Electric',
      fuel: 'Fuel',
      batteryPct: 'Battery %',
      fuelPct: 'Fuel %',
      odometerKm: 'Odometer (km)',
      updateScooter: 'Update scooter',
      addScooter: 'New scooter',
      details: 'Details',
      edit: 'Edit',
      created: 'Created',
      statusHistory: 'Scooter status history',
      columnScooterId: 'Scooter ID',
      columnBrand: 'Brand',
      columnModel: 'Model',
      columnPlateNumber: 'Plate Number',
      columnNickname: 'Nickname',
      columnStatus: 'Status',
      columnVehicleStatus: 'Type & Level',
      columnOdometer: 'Odometer',
      pictures: 'Pictures',
      noPhotos: 'No photos yet',
      noPhotosYet: 'No photos yet',
      allPhotos: 'All Photos',
      viewAllPhotos: 'View All Photos',
      damage: 'Damage',
      damageOnly: 'Damage',
      showingDamage: 'Showing Damage',
      noDamagePhotos: 'No photos with damage',
      markAsDamage: 'Mark as damage',
      markDamage: 'Mark Damage',
      removeDamage: 'Remove Damage',
      markNoDamage: 'Mark no damage',
      damageDetected: 'Damage detected',
      scooterPhoto: 'Scooter photo',
      selectImageFile: 'Please select an image file',
      fileSizeLimit: 'File size must be less than 10MB',
      photoUploaded: 'Photo uploaded successfully',
      photoDeleted: 'Photo deleted',
      photoDeleteFailed: 'Failed to delete photo',
      photoUpdateFailed: 'Failed to update photo',
      photoUploadFailed: 'Failed to upload photo',
      photoStorageNotConfigured: 'Photo storage is not configured. Please contact your administrator.',
      deletePhoto: 'Delete photo',
      drawCircle: 'Draw Circle',
      stopDrawing: 'Stop Drawing',
      undo: 'Undo',
      redo: 'Redo',
      capture: 'Capture',
      takePhoto: 'Take Photo',
      chooseFromGallery: 'Choose from Gallery',
      addPhoto: 'Add Photo',
      photoPreview: 'Photo Preview',
      cameraError: 'Failed to access camera',
      cameraPermissionDenied: 'Camera permission denied'
    },
    pages: {
      manageColumns: 'Manage columns',
      columns: 'Columns',
      retiredHidden: 'Retired scooters are hidden',
      failedLoadScooters: 'Failed to load scooters',
      checkConnection: 'Please check your connection and try again.',
      retry: 'Retry',
      clearFilters: 'Clear filters',
      addScooter: 'New scooter',
      noScootersYet: 'No scooters yet',
      noScootersMatch: 'No scooters match your filters',
      addFirstScooter: 'Add your first scooter to get started.',
      tryAdjusting: 'Try adjusting or clearing your filters.',
      rowsPerPage: 'Rows per page:',
      pageOf: 'Page {{current}} of {{total}}',
      prevShort: 'Prev',
      nextShort: 'Next',
      prev: 'Previous page',
      next: 'Next page'
    },
    // Contracts page labels
    contractsPage: {
      newContract: 'New Contract',
      showArchived: 'Show archived contracts',
      newContractSubtitle: 'Quickly create and review a new rental agreement',
      createdDate: 'Created Date',
      recentlyUpdated: 'Recently Updated',
      recentlyClosed: 'Recently Closed',
      allClosed: 'All Closed',
      showAll: 'Show All',
      showRecent: 'Show Recent',
      sortBy: 'Sort by...',
      archivedOnlyToDraft: 'Archived contracts can only be moved to Draft.'
    },
    activity: {
      title: 'Activity Log',
      subtitle: 'Monitor employee activity and system changes. View user-friendly logs for troubleshooting and auditing.',
      filterActivity: 'Filter Activity',
      timePeriod: 'Time Period',
      lastHour: 'Last Hour',
      today: 'Today',
      thisWeek: 'This Week',
      thisMonth: 'This Month',
      custom: 'Custom',
      from: 'From',
      to: 'To',
      employee: 'Employee',
      type: 'Activity Type',
      allEmployees: 'All employees',
      allActivities: 'All activities',
      scooterMgmt: 'Scooter Management',
      contractMgmt: 'Contract Management',
      clientMgmt: 'Client Management',
      orgSettings: 'Organization Settings',
      searchEverything: 'Search Everything',
      searchPlaceholder: 'Search names, IDs, prices, statuses...'
    },
    contracts: {
      filters: 'Filters',
      editContract: 'Edit Contract',
      dates: 'Dates',
      for: 'for',
      scooter: 'Scooter',
      start: 'Start',
      end: 'End',
      duration: 'Duration',
      days: 'days',
      weeks: 'weeks',
      months: 'months',
      estimatedTotal: 'Estimated total',
      fetchingData: 'Fetching clients and scooters',
      scooterCurrentlyRented: 'ℹ️ This scooter is currently rented{{until}}.',
      until: 'until',
      futureReservationNote: "You can still create a contract for a future reservation - just make sure the dates don't overlap.",
      searchClients: 'Search clients by name/email/id',
      noMatches: 'No matches',
      selected: 'Selected:',
      scooterQuery: 'Scooter (ID or model contains)',
      scooterPlaceholder: 'e.g. Ninebot or UUID',
      dailyRateRange: 'Daily rate (min/max)',
      min: 'Min',
      max: 'Max',
      minExceedsMax: 'Min cannot exceed Max',
      endAfterStart: 'End time must be after start time',
      clientRequired: 'Client is required',
      scooterRequired: 'Scooter is required',
      startTimeRequired: 'Start time is required',
      dailyRatePositive: 'Daily rate must be > 0',
      addContract: 'New contract',
      client: 'Client',
      selectClient: 'Select client',
      searchScooters: 'Search scooters...',
      startTime: 'Start time',
      status: 'Status',
      dailyRate: 'Daily rate',
      deposit: 'Deposit',
      endTime: 'End Date',
      contractId: 'Contract ID',
      paymentMethod: 'Payment Method',
      cash: 'Cash',
      local_bank_transfer: 'Local Bank Transfer',
      international_bank_transfer: 'International Bank Transfer',
      notes: 'Notes',
      notesPlaceholder: 'Optional notes about this contract',
      createDraftForScooter: 'Create a new contract draft for this scooter.',
      saveAsDraftHint: 'Save as draft for later activation',
      startImmediatelyHint: 'Start contract immediately',
      creating: 'Creating...',
      createDraft: 'Create draft',
      createActive: 'Create active',
      day: 'day',
      forDays: 'for {{count}} day',
      forDays_plural: 'for {{count}} days',
      total: 'Total:',
      dayOf: 'day {{day}} of {{total}}'
    },
    contractsUi: {
      renting: 'renting',
      statusHistory: 'Status history',
      noHistory: 'No history available',
      created: 'Created',
      overdue: 'Overdue',
      overdueTitle: 'Contract is overdue',
      remainingTitle: 'Time remaining until contract end',
      remaining: 'left',
      unitDayShort: 'd',
      unitHourShort: 'h',
      unitMinuteShort: 'm',
      clientDetails: 'Client Details',
      scooterDetails: 'Scooter Details',
      clientNotFound: 'Client not found',
      scooterNotFound: 'Scooter not found',
      fullName: 'Full Name',
      localPhone: 'Local Phone',
      messenger: 'WhatsApp',
      brand: 'Brand',
      vehicleType: 'Vehicle type',
      battery: 'Battery',
      qrCode: 'QR Code',
      scooterStatus: 'Scooter status',
      noStatusChanges: 'No status changes yet',
      loadingHistory: 'Loading history...',
      characters: 'characters',
      back: 'Back',
      startDate: 'Start Date',
      endDate: 'End Date',
      contractId: 'Contract ID',
      editContract: 'Edit Contract',
      duplicate: 'Duplicate',
      duplicateContract: 'Duplicate contract',
      duplicateSubtitle: 'Choose start, duration, and end for the new draft',
      endTimeMustBeAfter: 'End time must be after start time',
      changeStatus: 'Change status',
      currentStatus: 'current',
      changeScooterStatus: 'Change scooter status',
      currentScooterStatus: 'current',
      showingRange: 'Showing {{start}}–{{end}} of {{total}}',
      contractCreatedTitle: 'Contract created',
      contractCreatedMessage: 'Do you want to open the created contract?',
      contractLabel: 'Contract',
      openContract: 'Open Contract',
      stayHere: 'Stay Here',
      scooterStatusQuestion: 'What should be the status of scooter "{{model}}"?',
      currentStatusLabel: 'Current status:',
      statusChangeHelp: 'This will help you manage the scooter\'s availability after the contract status change.',
      working: 'Working…',
      moveContractAndUpdate: 'Move Contract & Update Scooter',
      enterToConfirm: 'Enter to confirm · Esc to cancel',
      statusAvailable: 'Available',
      statusAvailableDesc: 'Scooter is ready for rental',
      statusRented: 'Rented',
      statusRentedDesc: 'Scooter is currently being rented',
      statusMaintenance: 'Maintenance',
      statusMaintenanceDesc: 'Scooter needs maintenance',
      statusRetired: 'Retired',
      statusRetiredDesc: 'Scooter is no longer in service',
      statusCurrent: 'Current',
      moveContractTitle: 'Move Contract',
      moveContractMessage: 'Please select the new status for the scooter when moving this contract.',
      fromLabel: 'From',
      toLabel: 'To',
      clientLabel: 'Client',
      scooterLabel: 'Scooter',
      currentContract: 'Current contract:',
      viewContract: 'View Contract'
    },
    contractDocument: {
      print: 'Print',
      saveAsPDF: 'Save as PDF',
      email: 'Email',
      addSignature: 'Add Signature',
      emailRequired: 'Email address is required',
      emailSent: 'Contract sent successfully',
      enterSignatureName: 'Enter signer name to append to document',
      loading: 'Loading contract...',
      preparingPDF: 'Preparing PDF...',
      downloadSuccess: 'PDF downloaded successfully',
      downloadFailed: 'Failed to generate PDF',
      retry: 'Retry',
      emailContract: 'Email Contract',
      recipientEmail: 'Recipient Email',
      sending: 'Sending...',
      send: 'Send'
    },
    businessDetails: {
      title: 'Business Details',
      settingsUpdated: 'Business details updated successfully',
      errorLoading: 'Error loading business details.',
      helpText: 'Fill in your business information below. These details will appear on rental contracts. Leave fields blank if you prefer to fill them by hand on printed contracts.',
      businessName: 'Business Name',
      businessNamePlaceholder: 'Enter your business name',
      businessAddress: 'Business Address',
      businessAddressPlaceholder: 'Enter your business address',
      businessPhone: 'Business Phone',
      businessPhonePlaceholder: '+66 2 123 4567',
      businessEmail: 'Business Email',
      businessEmailPlaceholder: 'เช่น business@example.com',
      taxId: 'Tax ID / Business Registration',
      taxIdPlaceholder: '1234567890123',
      representativeName: 'Authorized Representative Name',
      representativeNamePlaceholder: 'John Smith',
      representativeIdNumber: 'Representative ID Number',
      representativeIdNumberPlaceholder: '1234567890123',
      saving: 'Saving...',
      saveDetails: 'Save Business Details',
      saved: 'Saved'
    },
    messaging: {
      title: 'Contact Client',
      scanQR: 'Scan QR Code',
      orClickLink: 'Or click link below',
      openApp: 'Open Messaging App',
      selectTemplate: 'Select Message Template',
      customMessage: 'Custom Message',
      messageEditor: 'Message Editor',
      thankYou: 'Thank You',
      reminder: 'Reminder',
      payment: 'Payment Reminder',
      templateThankYou: 'Thank you for choosing {{businessName}}! We hope you enjoyed your scooter rental experience. Looking forward to serving you again!',
      templateReminder: 'Hi {{clientName}}! This is {{businessName}}. Reminder: Your rental period ends tomorrow. Please return the scooter.',
      templatePayment: 'Hi {{clientName}}! This is {{businessName}}. Payment reminder for contract #{{contractId}}.',
      noWhatsApp: 'No WhatsApp number available',
      noLINE: 'No LINE ID available',
      close: 'Close'
    },
    finance: {
      title: 'Finance Overview',
      exportCSV: 'Export CSV',
      timeRange: 'Time Range',
      day: 'Day',
      week: 'Week',
      month: 'Month',
      year: 'Year',
      all: 'All',
      searchPlaceholder: 'Search scooters...',
      showSoldOnly: 'Show sold only',
      totalPurchase: 'Total Purchase',
      totalMaintenance: 'Total Maintenance',
      totalIncome: 'Total Income',
      netProfit: 'Net Profit',
      topROIScooter: 'Top ROI Scooter',
      bestBrandModel: 'Best Brand/Model',
      scooters: 'scooters',
      scooter: 'Scooter',
      purchase: 'Purchase',
      maintenance: 'Maintenance',
      income: 'Income',
      sale: 'Sale',
      net: 'Net',
      roi: 'ROI',
      status: 'Status',
      profitable: 'Profitable',
      breakingEven: 'Breaking Even',
      lossMaker: 'Loss-Maker',
      totals: 'Totals',
      recentExpenses: 'Recent Expenses',
      noExpenses: 'No expenses recorded',
      loadingExpenses: 'Loading expenses...',
      noExpensesInRange: 'No expenses in selected time range',
      expenseTotal: 'Total',
      categoryOilChange: 'Oil Change',
      categoryTires: 'Tires',
      categoryBrakes: 'Brakes',
      categoryEngine: 'Engine',
      categoryBattery: 'Battery',
      categoryBodyParts: 'Body Parts',
      categoryCleaning: 'Cleaning',
      categoryRegistration: 'Registration',
      categoryInsurance: 'Insurance',
      categoryMiscParts: 'Misc Parts',
      categoryCustom: 'Custom',
      affectsExpenseDetails: 'affects expense details',
      tableView: 'Table',
      chartsView: 'Charts',
      toggleKPIs: 'Toggle KPIs',
      toggleInsights: 'Toggle Insights',
      toggleAdditionalTables: 'Toggle Additional Tables',
      showOperational: 'Show operational view',
      showFull: 'Show full view',
      operationalView: 'Operational view',
      fullView: 'Full view',
      operationalNet: 'Operational net',
      date: 'Date',
      category: 'Category',
      amount: 'Amount',
      note: 'Notes',
      actions: 'Actions',
      edit: 'Edit',
      delete: 'Delete',
      editExpense: 'Edit Expense',
      customLabel: 'Custom label',
      confirmDeleteExpense: 'Are you sure you want to delete this expense?',
      expenseDeleted: 'Expense deleted',
      deleteFailed: 'Failed to delete expense',
      expenseUpdated: 'Expense updated',
      updateFailed: 'Failed to update expense',
      expenseCreated: 'Expense created',
      addExpense: 'Add Expense',
      brandModelPerformance: 'Brand/Model Performance',
      brand: 'Brand',
      model: 'Model',
      count: 'Count',
      avgROI: 'Avg ROI',
      topExpenses: 'Top Expenses by Category',
      noData: 'No data',
      monthlyTrends: 'Monthly Financial Trends',
      expenseBreakdown: 'Expense Category Breakdown',
      maintenanceEfficiency: 'Maintenance Efficiency',
      topPerformers: 'Top Performers',
      needsAttention: 'Needs Attention',
      fleetHealth: 'Fleet Health Overview',
      totalScooters: 'Total Scooters',
      revenueVsMaintenance: 'Revenue vs Maintenance by Scooter',
      profitTrend: 'Profit Trend by Scooter',
      statusDistribution: 'Status Distribution',
      exportBasic: 'Export Basic Summary',
      exportDetailed: 'Export Detailed Expenses',
      exportingDetailed: 'Exporting detailed expenses...',
      exportCompleted: 'Export completed',
      exportFailed: 'Failed to export'
    }
  },
  th: {
    theme: {
      dark: 'มืด',
      light: 'สว่าง',
      blue: 'สีน้ำเงิน',
      wood: 'ลายไม้'
    },
    home: {
      quickActions: 'ทางลัด',
      recentActivity: 'กิจกรรมล่าสุด',
      fleetStatusOverview: 'ภาพรวมสถานะรถ',
      welcomeTitle: 'ยินดีต้อนรับสู่ OurScooter',
      welcomeSubtitle: 'แดชบอร์ดจัดการฟลีตรถสกู๊ตเตอร์แบบครบวงจร',
      addScooter: 'รถสกู๊ตเตอร์ใหม่',
      registerScooter: 'เพิ่มรถคันใหม่',
      addClient: 'ลูกค้าใหม่',
      registerClient: 'เพิ่มลูกค้าใหม่',
      newContract: 'สร้างสัญญา',
      createAgreement: 'สร้างสัญญาเช่า',
      org: 'องค์กร',
      allOrgs: 'ทุกองค์กร',
      availableScooters: 'รถพร้อมให้เช่า',
      readyForRental: 'พร้อมปล่อยเช่า',
      rentedScooters: 'รถกำลังเช่า',
      currentlyInUse: 'กำลังใช้งาน',
      maintenance: 'ซ่อมบำรุง',
      underRepair: 'อยู่ระหว่างซ่อม',
      fleetOverview: 'ภาพรวมรถทั้งหมด',
      available: 'ว่าง',
      rented: 'เช่าอยู่',
      activeRevenue: 'รายได้ที่ใช้งานอยู่',
      monthlyRecurring: 'รายได้รายเดือน',
      totalClients: 'ลูกค้าทั้งหมด',
      activeContracts: 'สัญญาที่ใช้งานอยู่',
      avgBattery: 'แบตเตอรี่เฉลี่ย',
      fleetBatteryHealth: 'สุขภาพแบตเตอรี่รวม',
      avgFuel: 'น้ำมันเฉลี่ย',
      fuel: 'น้ำมัน',
      electric: 'ไฟฟ้า',
      fleetROI: 'ผลตอบแทนรถ',
      rev: 'รายได้',
      cost: 'ต้นทุน'
    },
    users: {
      title: 'ผู้ใช้',
      errorLoading: 'โหลดรายชื่อผู้ใช้ไม่สำเร็จ',
      id: 'ไอดี',
      email: 'อีเมล',
      role: 'สิทธิ์',
      org: 'องค์กร',
      roleSuperadmin: 'ผู้ดูแลระบบสูงสุด',
      roleAdmin: 'ผู้ดูแลระบบ',
      roleStaff: 'พนักงาน'
    },
    management: {
      title: 'การจัดการองค์กร',
      errorLoading: 'โหลดการตั้งค่าไม่สำเร็จ',
      timeFormat: 'รูปแบบเวลา',
      defaultCurrency: 'สกุลเงินเริ่มต้น',
      defaultCountryCode: 'รหัสประเทศเริ่มต้น',
      defaultDailyPrice: 'ราคาต่อวันเริ่มต้น',
      defaultDeposit: 'มัดจำสัญญาเริ่มต้น',
      defaultRentLength: 'ระยะเวลาเช่า (วัน)',
      defaultPaymentMethod: 'วิธีชำระเงินเริ่มต้น',
      buttonStyle: 'รูปแบบปุ่ม',
      buttonStyleDesc: 'เลือกรูปแบบปุ่มสำหรับทั้งระบบ',
      countryCodeHint: 'เช่น 66 สำหรับไทย, 1 สำหรับสหรัฐฯ',
      countryCodeExplain: 'กรอกเป็นตัวเลขเท่านั้น ใช้เติมหน้าหมายเลขโทรศัพท์ในฟอร์มลูกค้า',
      saving: 'กำลังบันทึก...',
      saveSettings: 'บันทึกการตั้งค่า',
      saved: 'บันทึกแล้ว',
      modernStyle: 'สไตล์สมัยใหม่',
      classicStyle: 'สไตล์คลาสสิก',
      originalStyle: 'สไตล์ดั้งเดิม',
      btnPrimary: 'ปุ่มหลัก',
      btnSecondary: 'ปุ่มรอง',
      btnGhost: 'โกสต์',
      btnDanger: 'อันตราย',
      modernDesc: 'พื้นหลังไล่สีพร้อมแอนิเมชันและมุมโค้งมน',
      classicDesc: 'ดีไซน์เรียบง่าย ใช้สีตามธีมและเส้นขอบบางๆ',
      originalDesc: 'ดีไซน์ปุ่มเดิมก่อนปรับปรุงโค้ด',
      timeFormatExample1: '15 กันยายน 2025 6:09 หลังเที่ยง',
      timeFormatExample2: 'กันยายน 15 2025 6:09 หลังเที่ยง',
      timeFormatExample3: '15 กันยายน 2025 18:09',
      timeFormatExample4: 'กันยายน 15 2025 18:09'
    },
    common: {
      language: 'ภาษา',
      theme: 'ธีม',
      save: 'บันทึก',
      cancel: 'ยกเลิก',
      logout: 'ออกจากระบบ',
      loading: 'กำลังโหลด...',
      reset: 'ล้างค่า',
      close: 'ปิด',
      back: 'ย้อนกลับ',
      delete: 'ลบ',
      clearAll: 'ล้างทั้งหมด',
      upload: 'อัปโหลด',
      uploading: 'กำลังอัปโหลด...',
      selectPhoto: 'เลือกรูปภาพ',
      addNotes: 'เพิ่มหมายเหตุ...',
      notes: 'หมายเหตุ',
      optional: 'ไม่บังคับ',
      retake: 'ถ่ายใหม่',
      usePhoto: 'ใช้รูปภาพ',
      viewAll: 'ดูรูปภาพทั้งหมด',
      confirmDelete: 'แน่ใจหรือไม่ว่าต้องการลบรูปภาพนี้?',
      add: 'เพิ่ม',
      copied: 'คัดลอกแล้ว',
      expand: 'ขยาย',
      collapse: 'ย่อ',
      updateFailed: 'อัปเดตไม่สำเร็จ',
      updateFailedWithMessage: 'อัปเดตไม่สำเร็จ: {{message}}',
      failedToSendEmail: 'ส่งอีเมลไม่สำเร็จ',
      statusChangeNotAllowed: 'ไม่สามารถเปลี่ยนสถานะนี้ได้',
      expenseAddedSuccessfully: 'เพิ่มค่าใช้จ่ายเรียบร้อย',
      edit: 'แก้ไข'
    },
    toasts: {
      contractCreated: 'สร้างสัญญาเรียบร้อย',
      contractUpdated: 'อัปเดตสัญญาเรียบร้อย',
      contractDeleted: 'ลบสัญญาเรียบร้อย',
      contractCreateFailed: 'สร้างสัญญาไม่สำเร็จ',
      contractUpdateFailed: 'อัปเดตสัญญาไม่สำเร็จ',
      contractDeleteFailed: 'ลบสัญญาไม่สำเร็จ',
      scooterCreated: 'เพิ่มรถเรียบร้อย',
      scooterUpdated: 'อัปเดตรถเรียบร้อย',
      scooterDeleted: 'ลบรถเรียบร้อย',
      scooterStatusUpdated: 'อัปเดตสถานะรถเรียบร้อย',
      scooterStatusUpdateFailed: 'อัปเดตสถานะรถไม่สำเร็จ',
      clientCreated: 'เพิ่มลูกค้าเรียบร้อย',
      clientUpdated: 'อัปเดตข้อมูลลูกค้าเรียบร้อย',
      clientDeleted: 'ลบลูกค้าเรียบร้อย',
      settingsUpdated: 'บันทึกการตั้งค่าเรียบร้อย',
      scooterStatusChangedAuto: 'ระบบเปลี่ยนสถานะรถให้อัตโนมัติ',
      scooterStatusChangeDesc: '{{scooterName}} เปลี่ยนสถานะจาก {{fromStatus}} เป็น {{toStatus}} {{reason}}',
      becauseContractActivated: 'เพราะสัญญาถูกเปิดใช้งาน',
      becauseContractDeactivated: 'เพราะสัญญาถูกปิดหรือเก็บถาวร',
      automatically: 'อัตโนมัติ',
      viewScooter: 'ดูรายละเอียดรถ',
      scooterOpenedFromNotif: 'เปิด {{scooterName}} แล้ว',
      scooterOpenedFromNotifDesc: 'เปิดรถคันนี้จากแจ้งเตือนการเปลี่ยนสถานะ',
      closedContractsMoveRestriction: 'สัญญาที่ปิดแล้วสามารถย้ายไปเก็บถาวรได้เท่านั้น'
    },
    login: {
      title: 'เข้าสู่ระบบ',
      email: 'อีเมล',
      username: 'ชื่อผู้ใช้',
      password: 'รหัสผ่าน',
      signIn: 'เข้าสู่ระบบ',
      signingIn: 'กำลังเข้าสู่ระบบ...',
      login: 'เข้าสู่ระบบ',
      loggingIn: 'กำลังเข้าสู่ระบบ...',
      loginFailed: 'เข้าสู่ระบบไม่สำเร็จ',
      language: 'ภาษา'
    },
    sidebar: {
      scooters: 'รถสกู๊ตเตอร์',
      clients: 'ลูกค้า',
      contracts: 'สัญญาเช่า',
      finance: 'การเงิน',
      users: 'ผู้ใช้',
      activity: 'บันทึกกิจกรรม',
      management: 'การจัดการ',
      howItWorks: 'วิธีใช้งาน',
      signIn: 'เข้าสู่ระบบ',
      expand: 'ขยายแถบข้าง',
      collapse: 'ย่อแถบข้าง',
      logoutConfirm: 'ต้องการออกจากระบบใช่ไหม?'
    },
    toolbar: {
      searchPlaceholder: 'ค้นหาไอดี รุ่น หรือสถานะ…',
      clear: 'ล้าง',
      listView: 'มุมมองรายการ',
      kanbanView: 'มุมมองคัมบัง',
      calendarView: 'ปฏิทิน',
      gridView: 'มุมมองกริด',
      mapView: 'มุมมองแผนที่',
      filter: 'ตัวกรอง',
      export: 'ส่งออก',
      total: 'ทั้งหมด {{count}} รายการ',
      queryLabel: 'ค้นหา',
      clearQuery: 'ล้างการค้นหา',
      removeFilter: 'ลบ {{filter}}'
    },
    ui: {
      zoom: 'ซูม'
    },
    calendar: {
      week: 'สัปดาห์',
      month: 'เดือน',
      all: 'ทั้งหมด',
      plusMore: '+{{count}} รายการ',
      scrollHint: 'กด Shift แล้วเลื่อนเมาส์เพื่อเลื่อนในแนวนอน',
      hideEmpty: 'ซ่อนรถที่ไม่มีสัญญาในช่วงนี้'
    },
    status: {
      available: 'พร้อมให้เช่า',
      rented: 'กำลังเช่า',
      maintenance: 'ซ่อมบำรุง',
      retired: 'ปลดระวาง',
      draft: 'ฉบับร่าง',
      active: 'ใช้งานอยู่',
      closed: 'ปิดแล้ว',
      archived: 'เก็บถาวร'
    },
    clients: {
      id: 'รหัสลูกค้า',
      name: 'ชื่อ',
      messenger: 'วอทส์แอป',
      newClient: 'ลูกค้าใหม่',
      creating: 'กำลังเพิ่มลูกค้า...',
      fullName: 'ชื่อ-นามสกุล',
      fullNamePlaceholder: 'กรอกชื่อ-นามสกุล',
      email: 'อีเมล',
      emailPlaceholder: 'กรอกอีเมล',
      localPhone: 'เบอร์โทรในประเทศ',
      localPhonePlaceholder: 'เบอร์โทร (ไม่บังคับ)',
      messengerNumber: 'เบอร์วอทส์แอป',
      countryCodePlaceholder: '66',
      messengerNumberPlaceholder: 'เบอร์วอทส์แอป (ไม่บังคับ)',
      lineId: 'ไลน์ไอดี',
      lineIdPlaceholder: 'ไลน์ไอดี (ไม่บังคับ)',
      whatsappButton: 'วอทส์แอป',
      lineButton: 'ไลน์',
      contactViaWhatsApp: 'ติดต่อผ่านวอทส์แอป',
      contactViaLINE: 'ติดต่อผ่านไลน์',
      notes: 'หมายเหตุ',
      notesPlaceholder: 'ข้อมูลเพิ่มเติม (เช่น เบอร์ต่างประเทศ)',
      addClient: 'ลูกค้าใหม่',
      detailsTitle: 'รายละเอียดลูกค้า',
      created: 'วันที่สร้าง',
      clientId: 'รหัสลูกค้า',
      edit: 'แก้ไขลูกค้า',
      previousContracts: 'สัญญาเก่า',
      noCurrentContracts: 'ยังไม่มีสัญญาปัจจุบัน',
      editClient: 'แก้ไขลูกค้า',
      newContractBtn: 'สร้างสัญญา',
      close: 'ปิด',
      statusHistory: 'ประวัติสถานะ'
    },
    scooters: {
      id: 'หมายเลขรถ',
      modelRequired: 'กรุณาระบุรุ่นรถ',
      batteryRange: 'แบตเตอรี่ต้องอยู่ระหว่าง 0–100%',
      odometerNegative: 'เลขไมล์ต้องไม่เป็นค่าติดลบ',
      fuelRange: 'เปอร์เซ็นต์น้ำมันต้องอยู่ระหว่าง 0–100%',
      incompatibleEnergy: 'รถไฟฟ้าไม่มีน้ำมัน และรถน้ำมันไม่มีแบตเตอรี่',
      editScooter: 'แก้ไขรถ',
      newScooter: 'เพิ่มรถ',
      updating: 'กำลังบันทึก...',
      creating: 'กำลังเพิ่มรถ...',
      model: 'รุ่น',
      modelPlaceholder: 'เช่น Ninebot Max',
      status: 'สถานะ',
      typeLevel: 'ประเภทและระดับ',
      odometer: 'เลขไมล์',
      qrCode: 'คิวอาร์โค้ด',
      qrPlaceholder: 'คิวอาร์โค้ด (ไม่บังคับ)',
      plateNumber: 'หมายเลขทะเบียน',
      platePlaceholder: 'หมายเลขทะเบียน (ไม่บังคับ)',
      nickname: 'ชื่อเล่น',
      nicknamePlaceholder: 'เช่น ฮอนด้าแดง #1',
      nicknameOptional: 'ชื่อเล่น (ไม่บังคับ)',
      brand: 'ยี่ห้อ',
      brandPlaceholder: 'ยี่ห้อ (ไม่บังคับ)',
      vehicleType: 'ประเภทพาหนะ',
      electric: 'ไฟฟ้า',
      fuel: 'น้ำมัน',
      batteryPct: 'แบตเตอรี่ %',
      fuelPct: 'น้ำมัน %',
      odometerKm: 'เลขไมล์ (กม.)',
      updateScooter: 'บันทึก',
      addScooter: 'เพิ่มรถ',
      details: 'รายละเอียด',
      edit: 'แก้ไข',
      created: 'วันที่สร้าง',
      statusHistory: 'ประวัติสถานะ',
      columnScooterId: 'หมายเลขรถ',
      columnBrand: 'ยี่ห้อ',
      columnModel: 'รุ่น',
      columnPlateNumber: 'หมายเลขทะเบียน',
      columnNickname: 'ชื่อเล่น',
      columnStatus: 'สถานะ',
      columnVehicleStatus: 'ประเภทและระดับ',
      columnOdometer: 'เลขไมล์',
      pictures: 'รูปภาพ',
      noPhotos: 'ยังไม่มีรูปภาพ',
      noPhotosYet: 'ยังไม่มีรูปภาพ',
      allPhotos: 'รูปภาพทั้งหมด',
      viewAllPhotos: 'ดูรูปภาพทั้งหมด',
      damage: 'ความเสียหาย',
      damageOnly: 'ความเสียหาย',
      showingDamage: 'แสดงความเสียหาย',
      noDamagePhotos: 'ไม่มีรูปภาพที่มีความเสียหาย',
      markAsDamage: 'ทำเครื่องหมายว่ามีความเสียหาย',
      markDamage: 'ทำเครื่องหมายความเสียหาย',
      removeDamage: 'ลบเครื่องหมายความเสียหาย',
      markNoDamage: 'ทำเครื่องหมายว่าไม่มีความเสียหาย',
      damageDetected: 'พบความเสียหาย',
      scooterPhoto: 'รูปภาพรถสกู๊ตเตอร์',
      selectImageFile: 'กรุณาเลือกไฟล์รูปภาพ',
      fileSizeLimit: 'ขนาดไฟล์ต้องไม่เกิน 10MB',
      photoUploaded: 'อัปโหลดรูปภาพเรียบร้อย',
      photoDeleted: 'ลบรูปภาพเรียบร้อย',
      photoDeleteFailed: 'ลบรูปภาพไม่สำเร็จ',
      photoUpdateFailed: 'อัปเดตรูปภาพไม่สำเร็จ',
      photoUploadFailed: 'อัปโหลดรูปภาพไม่สำเร็จ',
      photoStorageNotConfigured: 'ยังไม่ได้ตั้งค่าระบบจัดเก็บรูปภาพ กรุณาติดต่อผู้ดูแลระบบ',
      deletePhoto: 'ลบรูปภาพ',
      drawCircle: 'วาดวงกลม',
      stopDrawing: 'หยุดวาด',
      undo: 'ยกเลิก',
      redo: 'ทำซ้ำ',
      capture: 'ถ่ายภาพ',
      takePhoto: 'ถ่ายภาพ',
      chooseFromGallery: 'เลือกจากแกลเลอรี',
      addPhoto: 'เพิ่มรูปภาพ',
      photoPreview: 'ตัวอย่างรูปภาพ',
      cameraError: 'ไม่สามารถเข้าถึงกล้องได้',
      cameraPermissionDenied: 'ปฏิเสธการเข้าถึงกล้อง'
    },
    pages: {
      manageColumns: 'จัดการคอลัมน์',
      columns: 'คอลัมน์',
      retiredHidden: 'ซ่อนรถที่ปลดระวาง',
      failedLoadScooters: 'โหลดรายการรถไม่สำเร็จ',
      checkConnection: 'กรุณาตรวจสอบการเชื่อมต่อแล้วลองอีกครั้ง',
      retry: 'ลองใหม่',
      clearFilters: 'ล้างตัวกรอง',
      addScooter: 'รถสกู๊ตเตอร์ใหม่',
      noScootersYet: 'ยังไม่มีรถ',
      noScootersMatch: 'ไม่พบรถที่ตรงกับตัวกรอง',
      addFirstScooter: 'เพิ่มรถคันแรกเพื่อเริ่มใช้งาน',
      tryAdjusting: 'ลองปรับหรือล้างตัวกรองดู',
      rowsPerPage: 'แถวต่อหน้า',
      pageOf: 'หน้า {{current}} จาก {{total}}',
      prevShort: 'ก่อนหน้า',
      nextShort: 'ถัดไป',
      prev: 'หน้าก่อนหน้า',
      next: 'หน้าถัดไป'
    },
    // Contracts page labels (Thai)
    contractsPage: {
      newContract: 'สร้างสัญญา',
      showArchived: 'แสดงสัญญาที่เก็บถาวร',
      newContractSubtitle: 'สร้างและตรวจสอบสัญญาเช่าได้อย่างรวดเร็ว',
      createdDate: 'วันที่สร้าง',
      recentlyUpdated: 'อัปเดตล่าสุด',
      recentlyClosed: 'ปิดล่าสุด',
      allClosed: 'ปิดทั้งหมด',
      showAll: 'แสดงทั้งหมด',
      showRecent: 'แสดงล่าสุด',
      sortBy: 'จัดเรียงตาม...',
      archivedOnlyToDraft: 'สัญญาที่เก็บถาวรสามารถย้ายไปเป็นฉบับร่างได้เท่านั้น'
    },
    activity: {
      title: 'บันทึกกิจกรรม',
      subtitle: 'ติดตามกิจกรรมของพนักงานและการเปลี่ยนแปลงของระบบ เพื่อตรวจสอบและแก้ไขปัญหา',
      filterActivity: 'ตัวกรองกิจกรรม',
      timePeriod: 'ช่วงเวลา',
      lastHour: 'ชั่วโมงที่ผ่านมา',
      today: 'วันนี้',
      thisWeek: 'สัปดาห์นี้',
      thisMonth: 'เดือนนี้',
      custom: 'กำหนดเอง',
      from: 'จาก',
      to: 'ถึง',
      employee: 'พนักงาน',
      type: 'ประเภทกิจกรรม',
      allEmployees: 'พนักงานทั้งหมด',
      allActivities: 'ทุกกิจกรรม',
      scooterMgmt: 'จัดการรถสกู๊ตเตอร์',
      contractMgmt: 'จัดการสัญญา',
      clientMgmt: 'จัดการลูกค้า',
      orgSettings: 'ตั้งค่าองค์กร',
      searchEverything: 'ค้นหาทั้งหมด',
      searchPlaceholder: 'ค้นหาชื่อ ไอดี ราคา สถานะ...'
    },
    contracts: {
      filters: 'ตัวกรอง',
      editContract: 'แก้ไขสัญญา',
      dates: 'วันเวลา',
      for: 'สำหรับ',
      scooter: 'รถสกู๊ตเตอร์',
      start: 'เริ่ม',
      end: 'สิ้นสุด',
      duration: 'ระยะเวลา',
      days: 'วัน',
      weeks: 'สัปดาห์',
      months: 'เดือน',
      estimatedTotal: 'ราคารวมโดยประมาณ',
      fetchingData: 'กำลังโหลดข้อมูลลูกค้าและรถ...',
      scooterCurrentlyRented: 'ℹ️ รถคันนี้กำลังเช่าอยู่{{until}}',
      until: 'ถึง',
      futureReservationNote: 'สามารถสร้างสัญญาสำหรับการจองล่วงหน้าได้ แต่ต้องตรวจสอบให้แน่ใจว่าเวลาไม่ทับซ้อนกัน',
      searchClients: 'ค้นหาลูกค้าด้วยชื่อ/อีเมล/ไอดี',
      noMatches: 'ไม่พบรายการ',
      selected: 'เลือกแล้ว',
      scooterQuery: 'รถสกู๊ตเตอร์ (ค้นหาไอดีหรือรุ่น)',
      scooterPlaceholder: 'เช่น Ninebot หรือ UUID',
      dailyRateRange: 'ราคาต่อวัน (ต่ำสุด/สูงสุด)',
      min: 'ต่ำสุด',
      max: 'สูงสุด',
      minExceedsMax: 'ค่าต่ำสุดต้องไม่มากกว่าค่าสูงสุด',
      endAfterStart: 'เวลาสิ้นสุดต้องอยู่หลังเวลาเริ่ม',
      clientRequired: 'กรุณาเลือกลูกค้า',
      scooterRequired: 'กรุณาเลือกรถ',
      startTimeRequired: 'กรุณาระบุเวลาเริ่ม',
      dailyRatePositive: 'ราคาต่อวันต้องมากกว่า 0',
      addContract: 'สัญญาใหม่',
      client: 'ลูกค้า',
      selectClient: 'เลือกลูกค้า',
      searchScooters: 'ค้นหารถ...',
      startTime: 'เวลาเริ่ม',
      status: 'สถานะ',
      dailyRate: 'ราคาต่อวัน',
      deposit: 'มัดจำ',
      endTime: 'เวลาสิ้นสุด',
      contractId: 'รหัสสัญญา',
      paymentMethod: 'วิธีชำระเงิน',
      cash: 'เงินสด',
      local_bank_transfer: 'โอนธนาคารในประเทศ',
      international_bank_transfer: 'โอนธนาคารระหว่างประเทศ',
      notes: 'หมายเหตุ',
      notesPlaceholder: 'หมายเหตุเพิ่มเติม',
      createDraftForScooter: 'สร้างสัญญาฉบับร่างสำหรับรถคันนี้',
      saveAsDraftHint: 'บันทึกเป็นฉบับร่างเพื่อเปิดใช้งานภายหลัง',
      startImmediatelyHint: 'เริ่มสัญญาทันที',
      creating: 'กำลังสร้าง...',
      createDraft: 'สร้างเป็นฉบับร่าง',
      createActive: 'สร้างและเปิดใช้งาน',
      day: 'วัน',
      forDays: 'เป็นเวลา {{count}} วัน',
      forDays_plural: 'เป็นเวลา {{count}} วัน',
      total: 'รวม',
      dayOf: 'วันที่ {{day}} จาก {{total}}'
    },
    contractsUi: {
      renting: 'เช่า',
      statusHistory: 'ประวัติสถานะ',
      noHistory: 'ยังไม่มีประวัติการเปลี่ยนแปลง',
      created: 'สร้าง',
      overdue: 'เกินกำหนด',
      overdueTitle: 'สัญญาเกินกำหนด',
      remainingTitle: 'เวลาที่เหลือจนสิ้นสุดสัญญา',
      remaining: 'เหลือ',
      unitDayShort: 'วัน',
      unitHourShort: 'ชม.',
      unitMinuteShort: 'นาที',
      clientDetails: 'รายละเอียดลูกค้า',
      scooterDetails: 'รายละเอียดรถ',
      clientNotFound: 'ไม่พบข้อมูลลูกค้า',
      scooterNotFound: 'ไม่พบข้อมูลรถ',
      fullName: 'ชื่อ-นามสกุล',
      localPhone: 'เบอร์โทรในประเทศ',
      messenger: 'วอทส์แอป',
      brand: 'ยี่ห้อ',
      vehicleType: 'ประเภทพาหนะ',
      battery: 'แบตเตอรี่',
      qrCode: 'คิวอาร์โค้ด',
      scooterStatus: 'สถานะรถ',
      noStatusChanges: 'ยังไม่มีการเปลี่ยนสถานะ',
      loadingHistory: 'กำลังโหลดประวัติ...',
      characters: 'ตัวอักษร',
      back: 'ย้อนกลับ',
      startDate: 'วันเริ่มต้น',
      endDate: 'วันสิ้นสุด',
      contractId: 'รหัสสัญญา',
      editContract: 'แก้ไขสัญญา',
      duplicate: 'ทำสำเนา',
      duplicateContract: 'ทำสำเนาสัญญา',
      duplicateSubtitle: 'เลือกวันเริ่ม ระยะเวลา และวันสิ้นสุดสำหรับฉบับร่างใหม่',
      endTimeMustBeAfter: 'เวลาสิ้นสุดต้องอยู่หลังเวลาเริ่มต้น',
      changeStatus: 'เปลี่ยนสถานะ',
      currentStatus: 'ปัจจุบัน',
      changeScooterStatus: 'เปลี่ยนสถานะรถ',
      currentScooterStatus: 'ปัจจุบัน',
      showingRange: 'แสดง {{start}}–{{end}} จาก {{total}}',
      contractCreatedTitle: 'สร้างสัญญาเรียบร้อย',
      contractCreatedMessage: 'ต้องการเปิดดูสัญญาที่สร้างไหม?',
      contractLabel: 'สัญญา',
      openContract: 'เปิดดูสัญญา',
      stayHere: 'อยู่หน้านี้',
      scooterStatusQuestion: 'ต้องการให้รถ "{{model}}" มีสถานะอะไร?',
      currentStatusLabel: 'สถานะปัจจุบัน',
      statusChangeHelp: 'ช่วยให้คุณจัดการความพร้อมของรถหลังการเปลี่ยนสถานะสัญญา',
      working: 'กำลังดำเนินการ...',
      moveContractAndUpdate: 'ย้ายสัญญาและอัปเดตรถ',
      enterToConfirm: 'กด Enter เพื่อยืนยัน หรือ Esc เพื่อยกเลิก',
      statusAvailable: 'พร้อมให้เช่า',
      statusAvailableDesc: 'รถพร้อมปล่อยเช่า',
      statusRented: 'กำลังเช่า',
      statusRentedDesc: 'รถกำลังถูกเช่าอยู่',
      statusMaintenance: 'ซ่อมบำรุง',
      statusMaintenanceDesc: 'รถต้องการการซ่อมบำรุง',
      statusRetired: 'ปลดระวาง',
      statusRetiredDesc: 'รถหมดสภาพแล้ว',
      statusCurrent: 'ปัจจุบัน',
      moveContractTitle: 'ย้ายสัญญา',
      moveContractMessage: 'กรุณาเลือกสถานะใหม่สำหรับรถเมื่อย้ายสัญญานี้',
      fromLabel: 'จาก',
      toLabel: 'ไปยัง',
      clientLabel: 'ลูกค้า',
      scooterLabel: 'รถ',
      currentContract: 'สัญญาปัจจุบัน',
      viewContract: 'ดูสัญญา'
    },
    contractDocument: {
      print: 'พิมพ์',
      saveAsPDF: 'บันทึกเป็น PDF',
      email: 'อีเมล',
      addSignature: 'เพิ่มลายเซ็น',
      emailRequired: 'กรุณากรอกอีเมล',
      emailSent: 'ส่งสัญญาเรียบร้อย',
      enterSignatureName: 'กรอกชื่อผู้ลงนามเพื่อเพิ่มในเอกสาร',
      loading: 'กำลังโหลดสัญญา...',
      preparingPDF: 'กำลังเตรียมไฟล์ PDF...',
      downloadSuccess: 'ดาวน์โหลด PDF เรียบร้อย',
      downloadFailed: 'สร้างไฟล์ PDF ไม่สำเร็จ',
      retry: 'ลองอีกครั้ง',
      emailContract: 'ส่งสัญญาทางอีเมล',
      recipientEmail: 'อีเมลผู้รับ',
      sending: 'กำลังส่ง...',
      send: 'ส่ง'
    },
    businessDetails: {
      title: 'ข้อมูลธุรกิจ',
      settingsUpdated: 'บันทึกข้อมูลธุรกิจเรียบร้อย',
      errorLoading: 'โหลดข้อมูลธุรกิจไม่สำเร็จ',
      helpText: 'กรอกข้อมูลธุรกิจด้านล่าง ข้อมูลนี้จะแสดงในสัญญาเช่า หากเว้นว่าง ระบบจะแสดงช่องว่างเพื่อให้กรอกด้วยลายมือเมื่อพิมพ์',
      businessName: 'ชื่อธุรกิจ',
      businessNamePlaceholder: 'กรอกชื่อธุรกิจ',
      businessAddress: 'ที่อยู่ธุรกิจ',
      businessAddressPlaceholder: 'กรอกที่อยู่ธุรกิจ',
      businessPhone: 'เบอร์โทรธุรกิจ',
      businessPhonePlaceholder: '+66 2 123 4567',
      businessEmail: 'อีเมลธุรกิจ',
      businessEmailPlaceholder: 'business@example.com',
      taxId: 'เลขภาษี / เลขทะเบียนธุรกิจ',
      taxIdPlaceholder: '1234567890123',
      representativeName: 'ชื่อผู้มีอำนาจลงนาม',
      representativeNamePlaceholder: 'สมชาย ใจดี',
      representativeIdNumber: 'เลขบัตรประชาชนผู้แทน',
      representativeIdNumberPlaceholder: '1234567890123',
      saving: 'กำลังบันทึก...',
      saveDetails: 'บันทึกข้อมูลธุรกิจ',
      saved: 'บันทึกแล้ว'
    },
    messaging: {
      title: 'ติดต่อลูกค้า',
      scanQR: 'สแกน QR Code',
      orClickLink: 'หรือคลิกลิงก์ด้านล่าง',
      openApp: 'เปิดแอปแชต',
      selectTemplate: 'เลือกข้อความสำเร็จรูป',
      customMessage: 'ข้อความกำหนดเอง',
      thankYou: 'ขอบคุณ',
      reminder: 'เตือนสัญญา',
      payment: 'เตือนชำระเงิน',
      templateThankYou: 'ขอบคุณที่เลือกใช้บริการ {{businessName}}! หวังว่าคุณจะได้รับประสบการณ์ที่ดีในการเช่ารถสกู๊ตเตอร์ หวังว่าจะได้บริการคุณอีกครั้ง',
      templateReminder: 'สวัสดีคุณ {{clientName}}! นี่คือ {{businessName}} เตือนว่าระยะเวลาเช่าจะสิ้นสุดพรุ่งนี้ กรุณาคืนรถด้วย',
      templatePayment: 'สวัสดีคุณ {{clientName}}! นี่คือ {{businessName}} เตือนชำระเงินสำหรับสัญญา #{{contractId}}',
      noWhatsApp: 'ไม่มีเบอร์วอทส์แอป',
      noLINE: 'ไม่มีไลน์ไอดี',
      close: 'ปิด'
    },
    finance: {
      title: 'ภาพรวมการเงิน',
      exportCSV: 'ส่งออก CSV',
      timeRange: 'ช่วงเวลา',
      day: 'วัน',
      week: 'สัปดาห์',
      month: 'เดือน',
      year: 'ปี',
      all: 'ทั้งหมด',
      searchPlaceholder: 'ค้นหารถ...',
      showSoldOnly: 'แสดงเฉพาะที่ขายแล้ว',
      totalPurchase: 'ค่าซื้อรวม',
      totalMaintenance: 'ค่าซ่อมบำรุงรวม',
      totalIncome: 'รายได้รวม',
      netProfit: 'กำไรสุทธิ',
      topROIScooter: 'รถที่ให้ผลตอบแทนสูงสุด',
      bestBrandModel: 'ยี่ห้อ/รุ่นที่ดีที่สุด',
      scooters: 'รถ',
      scooter: 'รถ',
      purchase: 'ค่าซื้อ',
      maintenance: 'ซ่อมบำรุง',
      income: 'รายได้',
      sale: 'ราคาขาย',
      net: 'กำไร/ขาดทุน',
      roi: 'ผลตอบแทน',
      status: 'สถานะ',
      profitable: 'ทำกำไร',
      breakingEven: 'คืนทุน',
      lossMaker: 'ขาดทุน',
      totals: 'รวม',
      recentExpenses: 'ค่าใช้จ่ายล่าสุด',
      noExpenses: 'ยังไม่มีค่าใช้จ่าย',
      loadingExpenses: 'กำลังโหลดค่าใช้จ่าย...',
      noExpensesInRange: 'ไม่มีค่าใช้จ่ายในช่วงเวลาที่เลือก',
      expenseTotal: 'รวม',
      categoryOilChange: 'เปลี่ยนน้ำมัน',
      categoryTires: 'ยาง',
      categoryBrakes: 'เบรก',
      categoryEngine: 'เครื่องยนต์',
      categoryBattery: 'แบตเตอรี่',
      categoryBodyParts: 'ตัวถัง',
      categoryCleaning: 'ทำความสะอาด',
      categoryRegistration: 'จดทะเบียน',
      categoryInsurance: 'ประกันภัย',
      categoryMiscParts: 'อะไหล่อื่นๆ',
      categoryCustom: 'กำหนดเอง',
      affectsExpenseDetails: 'ส่งผลต่อรายละเอียดค่าใช้จ่าย',
      tableView: 'ตาราง',
      chartsView: 'กราฟ',
      toggleKPIs: 'สลับตัวชี้วัด',
      toggleInsights: 'สลับข้อมูลเชิงลึก',
      toggleAdditionalTables: 'สลับตารางเพิ่มเติม',
      showOperational: 'แสดงมุมมองการดำเนินงาน',
      showFull: 'แสดงมุมมองเต็มรูปแบบ',
      operationalView: 'มุมมองการดำเนินงาน',
      fullView: 'มุมมองเต็มรูปแบบ',
      operationalNet: 'กำไรจากการดำเนินงาน',
      date: 'วันที่',
      category: 'หมวดหมู่',
      amount: 'จำนวนเงิน',
      note: 'หมายเหตุ',
      actions: 'การดำเนินการ',
      edit: 'แก้ไข',
      delete: 'ลบ',
      editExpense: 'แก้ไขค่าใช้จ่าย',
      customLabel: 'ป้ายกำกับกำหนดเอง',
      confirmDeleteExpense: 'แน่ใจหรือไม่ว่าต้องการลบค่าใช้จ่ายนี้?',
      expenseDeleted: 'ลบค่าใช้จ่ายแล้ว',
      deleteFailed: 'ลบค่าใช้จ่ายไม่สำเร็จ',
      expenseUpdated: 'อัปเดตค่าใช้จ่ายแล้ว',
      updateFailed: 'อัปเดตค่าใช้จ่ายไม่สำเร็จ',
      expenseCreated: 'สร้างค่าใช้จ่ายแล้ว',
      addExpense: 'เพิ่มค่าใช้จ่าย',
      brandModelPerformance: 'ประสิทธิภาพยี่ห้อ/รุ่น',
      brand: 'ยี่ห้อ',
      model: 'รุ่น',
      count: 'จำนวน',
      avgROI: 'ผลตอบแทนเฉลี่ย',
      topExpenses: 'ค่าใช้จ่ายสูงสุดตามหมวดหมู่',
      noData: 'ไม่มีข้อมูล',
      monthlyTrends: 'แนวโน้มทางการเงินรายเดือน',
      expenseBreakdown: 'รายละเอียดค่าใช้จ่ายตามหมวดหมู่',
      maintenanceEfficiency: 'ประสิทธิภาพการซ่อมบำรุง',
      topPerformers: 'ผลการดำเนินงานที่ดีที่สุด',
      needsAttention: 'ต้องการความสนใจ',
      fleetHealth: 'ภาพรวมสุขภาพฟลีต',
      totalScooters: 'รถทั้งหมด',
      revenueVsMaintenance: 'รายได้เทียบกับค่าซ่อมบำรุงตามรถ',
      profitTrend: 'แนวโน้มกำไรตามรถ',
      statusDistribution: 'การกระจายสถานะ',
      exportBasic: 'ส่งออกสรุปพื้นฐาน',
      exportDetailed: 'ส่งออกค่าใช้จ่ายรายละเอียด',
      exportingDetailed: 'กำลังส่งออกค่าใช้จ่ายรายละเอียด...',
      exportCompleted: 'ส่งออกเสร็จสิ้น',
      exportFailed: 'ส่งออกไม่สำเร็จ'
    },
    landing: {
      heroTitle: 'ปี 2026 แล้ว ยังใช้สเปรดชีตอยู่หรือเปล่า',
      heroSubtitle: 'เปลี่ยนมาใช้ระบบดิจิทัลกันเถอะ',
      heroDescription: 'แพลตฟอร์มเช่ารถสกู๊ตเตอร์ที่ใช้งานได้จริง',
      heroDescription2: 'หยุดเสียเงิน เริ่มทำอย่างมีเหตุผล',
      ctaButton: 'ดูการทำงาน →',
      ctaLink: 'หรือทำบนกระดาษต่อไป ตามใจคุณ',
      contractTimeLabel: 'เวลาเฉลี่ยในการสร้างสัญญา',
      contractTimeHint: 'นี่คือความเร็วที่คุณสร้างสัญญาได้เลย ไม่ต้องรอ ไม่มีแล็ก',
      problemsTitle: 'คุณอาจทำผิดอยู่',
      problemsSubtitle: '(เราพูดตามตรง)',
      problem1Title: 'สเปรดชีตไม่ใช่โมเดลธุรกิจ',
      problem1Desc: 'ถ้า Excel ล่ม ธุรกิจคุณก็ล่ม นั่นไม่ใช่ความยืดหยุ่น นั่นคือการเสี่ยงกับรายได้ของคุณ ตื่นได้แล้ว - นี่ไม่ใช่ปี 2015 แล้ว',
      problem2Title: 'คุณกำลังเสีย 15-30% ไปกับข้อผิดพลาดด้วยมือ',
      problem2Desc: 'รถจองซ้ำ การต่ออายุที่พลาด สัญญาที่หายไป ทุกขั้นตอนที่ทำด้วยมือคือเงินที่ไหลออกประตู หยุดได้แล้ว',
      problem3Title: '\'ระบบ\' ของคุณคือ WhatsApp + ความจำ',
      problem3Desc: 'ข้อความลูกค้ากระจายไป 3 แอป วันที่สัญญาอยู่ในหัว การติดตามการชำระเงินอยู่ในสมุดบันทึก นี่ไม่ใช่ปี 2010 แล้ว ตามทันหน่อย',
      problem4Title: 'คุณไม่สามารถขยายสิ่งที่คุณวัดไม่ได้',
      problem4Desc: 'รถคันไหนทำกำไรมากที่สุด? ลูกค้าคนไหนเช่านานที่สุด? ROI จริงของคุณคือเท่าไหร่? ถ้าไม่รู้ คุณกำลังบินตาบอด และนั่นแพง',
      challengeTitle: 'คิดว่าเราผิด?',
      challengeText: 'พิสูจน์ให้ดู แสดงระบบกระดาษของคุณมา แล้วเราจะแสดงให้เห็นว่าปี 2026 เป็นอย่างไร',
      challengeButton: 'ดูว่าปี 2026 เป็นอย่างไร →',
      featuresTitle: 'สิ่งที่คุณจะได้ (ที่คุณไม่รู้ว่าคุณต้องการ)',
      featuresSubtitle: 'ฟังก์ชันจริง ความเร็วจริง ผลลัพธ์จริง',
      featuresSubtitle2: 'ไม่มีน้ำ ไม่มีแค่ข้อเท็จจริง',
      feature1Title: 'แดชบอร์ดฟลีตแบบเรียลไทม์',
      feature1Desc: 'เห็นทุกอย่าง รู้ทุกอย่าง ทำทุกอย่าง พร้อมใช้งาน เช่า ซ่อมบำรุง - ทั้งหมดในมุมมองเดียว ไม่ต้องรอให้ "ไปตรวจสอบบันทึก" อีกต่อไป',
      feature1Item1: 'อัปเดตสถานะแบบเรียลไทม์ทั่วทั้งฟลีต',
      feature1Item2: 'ติดตามรายได้ที่สมเหตุสมผล',
      feature1Item3: 'คำนวณ ROI ได้ง่าย ไม่ต้องใช้ปริญญาเอก',
      feature2Title: 'สัญญาที่ไม่แย่',
      feature2Desc: 'สร้างสัญญาได้ในไม่กี่วินาที ติดตามแบบเรียลไทม์ ไม่จองซ้ำ ไม่พลาดวันต่ออายุ ไม่ต้องสงสัยว่า "ใครมีรถ #42?" อีกต่อไป',
      feature2Item1: 'มุมมอง Kanban เพราะสเปรดชีตไม่ใช่ภาพ',
      feature2Item2: 'ตรวจจับความขัดแย้งอัตโนมัติ (ใช่ เราตรวจสอบ)',
      feature2Item3: 'วางแผนการส่งมอบที่ใช้งานได้จริง',
      feature3Title: 'การต่ออายุและการแจ้งเตือน',
      feature3Desc: 'แจ้งเตือนการต่ออายุประกัน การต่อทะเบียน ความคุ้มครอง วันที่สิ้นสุดสัญญา ติดตามทั้งหมดอัตโนมัติ แจ้งเตือนก่อนจะสาย เพราะประกันหมดอายุ = ปัญหาแพง และการต่ออายุสัญญาที่พลาด = รายได้ที่หายไป',
      feature3Item1: 'แจ้งเตือนการต่ออายุประกัน (อย่าปล่อยให้หมดอายุ)',
      feature3Item2: 'แจ้งเตือนสัญญาครบกำหนด (ไม่พลาดการต่ออายุอีกต่อไป)',
      feature3Item3: 'ป้ายสถานะ: ใช้งาน ใกล้ครบกำหนด เกินกำหนด (คุณจะรู้)',
      feature4Title: 'มุมมองปฏิทินที่สมเหตุสมผล',
      feature4Desc: 'ดูตารางฟลีตทั้งหมดในมุมมองเดียว รู้ว่ารถเช่าเมื่อไหร่ วางแผนล่วงหน้า ไม่จองซ้ำ ไม่สับสนว่า "สัญญานี้สิ้นสุดเมื่อไหร่?" อีกต่อไป',
      feature4Item1: 'ไทม์ไลน์ภาพของสัญญาทั้งหมด',
      feature4Item2: 'ดูความพร้อมใช้งานในมุมมองเดียว',
      feature4Item3: 'วางแผนการส่งมอบและการรับมอบอย่างมืออาชีพ',
      feature5Title: 'QR code = ติดต่อลูกค้าทันที',
      feature5Desc: 'สแกน QR code ส่งข้อความลูกค้าทันที ไม่ต้องค้นหาใน WhatsApp อีกต่อไป ไม่ต้องถามว่า "นี่คือใครอีกแล้ว?" อีกต่อไป แค่สแกน ส่งข้อความ เสร็จ นี่คือปี 2026 แล้ว ทำตัวให้เหมาะสม',
      feature5Item1: 'QR code บนรถทุกคัน',
      feature5Item2: 'สแกนครั้งเดียว = WhatsApp/Line พร้อมข้อความที่กรอกไว้ล่วงหน้า',
      feature5Item3: 'ไม่ต้องล่าหาข้อมูลติดต่อลูกค้าอีกต่อไป',
      qrPhoneLabel: 'หมายเลขโทรศัพท์',
      qrPhoneHint: 'ใส่หมายเลขใดก็ได้เพื่อทดสอบ (เช่น หมายเลขแฟนของภรรยาคุณ)',
      qrMessageLabel: 'ข้อความ',
      qrMessageHint: 'แก้ไขข้อความนี้เพื่อปรับแต่งสิ่งที่ลูกค้าเห็น',
      qrScanHint: 'สแกนด้วยโทรศัพท์เพื่อทดสอบ',
      qrClickLink: 'หรือคลิกเพื่อเปิด WhatsApp',
      feature6Title: 'ผู้ใช้ไม่จำกัด ความโปร่งใสเต็มรูปแบบ',
      feature6Desc: 'เพิ่มสมาชิกทีมได้มากเท่าที่ต้องการ ไม่มีค่าธรรมเนียมต่อผู้ใช้ แอดมินเห็นทุกอย่าง - ใครทำอะไร เมื่อไหร่ และทำไม บันทึกกิจกรรมครบถ้วน ไม่มีความลับ ไม่มีเซอร์ไพรส์',
      feature6Item1: 'ผู้ใช้ไม่จำกัด (จริงๆ ไม่มีขีดจำกัด)',
      feature6Item2: 'บันทึกกิจกรรมครบถ้วนสำหรับแอดมิน',
      feature6Item3: 'สิทธิ์ตามบทบาท (ผู้ใช้/แอดมิน)',
      feature7Title: 'การแจ้งเตือนประกันที่ช่วยคุณประหยัดเงิน',
      feature7Desc: 'อย่าปล่อยให้ประกันหมดอายุอีกต่อไป รับการแจ้งเตือนก่อนจะสาย หน้าต่างการต่ออายุที่กำหนดเอง การแจ้งเตือนอัจฉริยะ เพราะประกันหมดอายุ = ปัญหาแพง และเราไม่ชอบปัญหาที่แพง',
      feature7Item1: 'แจ้งเตือนการต่ออายุประกัน (ไม่พลาดกำหนดเวลา)',
      feature7Item2: 'หน้าต่างการแจ้งเตือนที่กำหนดเอง (7 วัน 30 วัน คุณเลือก)',
      feature7Item3: 'แจ้งเตือนแดชบอร์ดที่ดึงความสนใจจริงๆ',
      feature8Title: 'ติดตามการซ่อมบำรุงและวิเคราะห์ความสามารถในการทำกำไร',
      feature8Desc: 'ติดตามค่าใช้จ่ายทุกอย่าง รู้ว่ารถคันไหนทำเงินให้คุณ ดู ROI ต่อรถ ติดตามต้นทุนการซ่อมบำรุง ตัดสินใจตามข้อมูล หยุดเดา',
      feature8Item1: 'ติดตามค่าใช้จ่ายการซ่อมบำรุงครบถ้วน',
      feature8Item2: 'วิเคราะห์ความสามารถในการทำกำไรต่อรถ',
      feature8Item3: 'คำนวณ ROI ที่สำคัญจริงๆ',
      feature9Title: 'รูปภาพและมุมมองมือถือที่ใช้งานได้จริง',
      feature9Desc: 'ถ่ายรูประหว่างเดินทาง อินเทอร์เฟซที่เหมาะกับมือถือ รวมกล้อง แกลเลอรีรูปภาพต่อรถ ทำเครื่องหมายความเสียหายบนรูปภาพโดยตรง กรองตามสถานะความเสียหาย เพราะภาพหนึ่งภาพมีค่าพันคำ และพันคำจะไม่ช่วยคุณในศาล',
      feature9Item1: 'มุมมองมือถือที่เหมาะกับการทำงานภาคสนาม',
      feature9Item2: 'รวมกล้อง - ถ่ายรูปโดยตรงในแอป',
      feature9Item3: 'ทำเครื่องหมายความเสียหายบนรูปภาพด้วยตัวบ่งชี้ภาพ',
      feature9Item4: 'กรองรูปภาพตามสถานะความเสียหาย',
      feature10Title: 'สร้างสัญญาแบบมืออาชีพ',
      feature10Desc: 'กรอกฟิลด์ส่วนใหญ่อัตโนมัติ ดูตัวอย่างก่อนพิมพ์ มีเวอร์ชันไทยและอังกฤษ รูปแบบพร้อมพิมพ์ ไม่ต้องพิมพ์ด้วยมือ ไม่มีข้อผิดพลาด แค่สร้าง ดูตัวอย่าง พิมพ์ เสร็จ',
      feature10Item1: 'กรอกอัตโนมัติจากข้อมูลลูกค้าและรถ',
      feature10Item2: 'ดูตัวอย่างก่อนพิมพ์ (ไม่มีเซอร์ไพรส์)',
      feature10Item3: 'มีเวอร์ชันไทยและอังกฤษ',
      speedTitle: 'เร็วมากจนคุณคิดว่ามันพัง',
      speedDesc: 'React Query caching. Prefetch-on-hover. Token rehydration. Code splitting. Lazy loading. Optimistic updates.',
      speedDesc2: 'เราพูดภาษาของคุณ: ความเร็ว',
      speedMetric1: 'เวลาโหลดหน้า',
      speedMetric2: 'ค้นหาและกรอง',
      speedMetric3: 'รีเฟรชข้อมูล',
      finalCTATitle: 'พร้อมหยุดเสียเงินแล้วหรือยัง?',
      finalCTAText: 'หรือทำบนกระดาษต่อไป เราไม่ใช่เจ้านายคุณ',
      finalCTAText2: '(แต่รายได้ของคุณอาจดีกว่านี้ แค่บอก)',
      finalCTAButton: 'ดูการทำงาน →',
      finalCTALink: 'หรือบอกว่าเราผิด',
      viewFullContract: 'ดูสัญญาเต็มรูปแบบ'
    }
  },
  fil: {
    theme: {
      dark: 'Madilim',
      light: 'Maliwanag',
      blue: 'Asul',
      wood: 'Kahoy'
    },
    home: {
      quickActions: 'Mabilis na Aksyon',
      recentActivity: 'Kamakailang Aktibidad',
      fleetStatusOverview: 'Pangkalahatang Status ng Fleet',
      welcomeTitle: 'Maligayang pagdating sa OurScooter',
      welcomeSubtitle: 'Ang iyong komprehensibong dashboard para sa pamamahala ng scooter fleet',
      addScooter: 'Bagong Scooter',
      registerScooter: 'Magrehistro ng bagong scooter',
      addClient: 'Bagong Client',
      registerClient: 'Magrehistro ng bagong customer',
      newContract: 'Bagong Contract',
      createAgreement: 'Gumawa ng rental agreement',
      org: 'Org',
      allOrgs: 'Lahat ng org',
      availableScooters: 'Available na Scooters',
      readyForRental: 'Handa na para sa rental',
      rentedScooters: 'Nakarenta na Scooters',
      currentlyInUse: 'Kasalukuyang ginagamit',
      maintenance: 'Maintenance',
      underRepair: 'Nasa ilalim ng repair',
      fleetOverview: 'Pangkalahatang View ng Fleet',
      available: 'available',
      rented: 'nakarenta',
      activeRevenue: 'Aktibong Revenue',
      monthlyRecurring: 'Buwanang recurring revenue',
      totalClients: 'Kabuuang Clients',
      activeContracts: 'aktibong contracts',
      avgBattery: 'Avg Battery',
      fleetBatteryHealth: 'Kalusugan ng fleet battery',
      avgFuel: 'Avg Fuel',
      fuel: 'fuel',
      electric: 'electric',
      fleetROI: 'Fleet ROI',
      rev: 'rev',
      cost: 'cost'
    },
    users: {
      title: 'Users',
      errorLoading: 'Error sa pag-load ng users.',
      id: 'ID',
      email: 'Email',
      role: 'Role',
      org: 'Org',
      roleSuperadmin: 'Superadmin',
      roleAdmin: 'Admin',
      roleStaff: 'Staff'
    },
    management: {
      title: 'Pamamahala ng Organisasyon',
      errorLoading: 'Error sa pag-load ng settings.',
      timeFormat: 'Format ng Oras',
      defaultCurrency: 'Default na Currency',
      defaultCountryCode: 'Default na Country Code',
      defaultDailyPrice: 'Default na Daily Price',
      defaultDeposit: 'Default na Contract Deposit',
      defaultRentLength: 'Default na Rent Length (Days)',
      defaultPaymentMethod: 'Default na Payment Method',
      buttonStyle: 'Estilo ng Button',
      buttonStyleDesc: 'Pumili ng visual style para sa lahat ng buttons sa buong application.',
      countryCodeHint: '1 para sa USA, 44 para sa UK, atbp.',
      countryCodeExplain: 'Digits lang. Maglalagay ng prefix sa phone numbers sa client forms.',
      saving: 'Nagse-save...',
      saveSettings: 'I-save ang Settings',
      saved: 'Na-save na',
      modernStyle: 'Modernong Estilo',
      classicStyle: 'Klasikong Estilo',
      originalStyle: 'Orihinal na Estilo',
      btnPrimary: 'Primary',
      btnSecondary: 'Secondary',
      btnGhost: 'Ghost',
      btnDanger: 'Danger',
      modernDesc: 'Gradient backgrounds na may micro-interactions at rounded corners',
      classicDesc: 'Malinis, minimal design na may theme-aware colors at subtle borders',
      originalDesc: 'Ang original button design na nagustuhan mo bago ang refactoring',
      timeFormatExample1: '15 September 2025 6:09 PM',
      timeFormatExample2: 'September 15 2025 6:09 PM',
      timeFormatExample3: '15 September 2025 18:09',
      timeFormatExample4: 'September 15 2025 18:09'
    },
    common: {
      language: 'Language',
      theme: 'Theme',
      save: 'I-save',
      cancel: 'I-cancel',
      logout: 'Logout',
      loading: 'Naglo-load...',
      reset: 'I-reset',
      close: 'Isara',
      back: 'Bumalik',
      delete: 'Tanggalin',
      clearAll: 'I-clear lahat',
      upload: 'I-upload',
      uploading: 'Nag-u-upload...',
      selectPhoto: 'Pumili ng Photo',
      addNotes: 'Magdagdag ng notes...',
      notes: 'Notes',
      optional: 'Optional',
      retake: 'Kunin ulit',
      usePhoto: 'Gamitin ang Photo',
      viewAll: 'Tingnan lahat ng Photos',
      confirmDelete: 'Sigurado ka bang gusto mong tanggalin ang photo na ito?',
      add: 'Magdagdag',
      copied: 'Na-copy na',
      expand: 'I-expand',
      collapse: 'I-collapse',
      updateFailed: 'Nabigo ang pag-update',
      updateFailedWithMessage: 'Nabigo ang pag-update: {{message}}',
      failedToSendEmail: 'Nabigo ang pagpadala ng email',
      statusChangeNotAllowed: 'Hindi pinapayagan ang pagbabago ng status na ito',
      expenseAddedSuccessfully: 'Matagumpay na naidagdag ang expense',
      retry: 'Subukan ulit',
      edit: 'I-edit'
    },
    toasts: {
      contractCreated: 'Matagumpay na nagawa ang contract',
      contractUpdated: 'Matagumpay na na-update ang contract',
      contractDeleted: 'Matagumpay na natanggal ang contract',
      contractCreateFailed: 'Nabigo ang paggawa ng contract',
      contractUpdateFailed: 'Nabigo ang pag-update ng contract',
      contractDeleteFailed: 'Nabigo ang pagtanggal ng contract',
      scooterCreated: 'Matagumpay na nagawa ang scooter',
      scooterUpdated: 'Matagumpay na na-update ang scooter',
      scooterDeleted: 'Matagumpay na natanggal ang scooter',
      scooterStatusUpdated: 'Matagumpay na na-update ang scooter status',
      scooterStatusUpdateFailed: 'Nabigo ang pag-update ng scooter status',
      clientCreated: 'Matagumpay na nagawa ang client',
      clientUpdated: 'Matagumpay na na-update ang client',
      clientDeleted: 'Matagumpay na natanggal ang client',
      settingsUpdated: 'Matagumpay na na-update ang settings!',
      scooterStatusChangedAuto: 'Awtomatikong Nagbago ang Scooter Status',
      scooterStatusChangeDesc: 'Ang {{scooterName}} ay inilipat mula {{fromStatus}} patungo sa {{toStatus}} {{reason}}.',
      becauseContractActivated: 'dahil na-activate ang contract',
      becauseContractDeactivated: 'dahil na-close/archive ang contract',
      automatically: 'awtomatiko',
      viewScooter: 'Tingnan ang Scooter',
      scooterOpenedFromNotif: 'Binuksan ang {{scooterName}} details',
      scooterOpenedFromNotifDesc: 'Ang scooter na ito ay awtomatikong binuksan mula sa status change notification.',
      closedContractsMoveRestriction: 'Ang closed contracts ay maaari lang ilipat sa Archived.'
    },
    login: {
      title: 'Login',
      email: 'Email',
      username: 'Username',
      password: 'Password',
      signIn: 'Mag-sign in',
      signingIn: 'Nagse-sign in...',
      login: 'Login',
      loggingIn: 'Naglo-login...',
      loginFailed: 'Nabigo ang login',
      language: 'Language'
    },
    sidebar: {
      scooters: 'Mga Scooter',
      clients: 'Mga Client',
      contracts: 'Mga Contract',
      finance: 'Pananalapi',
      users: 'Mga User',
      activity: 'Tala ng Aktibidad',
      management: 'Pamamahala',
      howItWorks: 'Paano ito Gumagana',
      signIn: 'Mag-sign in',
      expand: 'I-expand ang sidebar',
      collapse: 'I-collapse ang sidebar',
      logoutConfirm: 'Sigurado ka bang gusto mong mag-logout?',
      menu: 'Menu'
    },
    howItWorks: {
      title: 'Paano Gamitin ang OurScooter (Para sa Staff)',
      subtitle: 'Simpleng hakbang, malinaw na patakaran, at kapaki-pakinabang na tip para gawin nang maayos at mahusay ang inyong trabaho.',
      whyTitle: 'Bakit OurScooter — layunin at benepisyo',
      whyDescription: 'Tumutulong ang OurScooter sa staff na patakbuhin nang mahusay ang pang-araw-araw na scooter rental operations: mas kaunting clicks, mas kaunting pagkakamali, at malinaw na records.',
      whySpeed: 'Bilis: gumawa ng contracts sa loob ng ilang segundo, muling gamitin ang mga detalye sa pamamagitan ng duplication.',
      whyClarity: 'Kalinawan: ipinapakita ng mga status kung nasaan ang bawat rental sa isang sulyap.',
      whyControl: 'Kontrol: i-filter, maghanap, at magpalit ng views (table/kanban) para umangkop sa gawain.',
      whyAccuracy: 'Kawastuhan: pinipigilan ng mga guardrails ang mga invalid moves at overlapping rentals.',
      signingInTitle: 'Pag-sign in at pagpapanatiling naka-sign in',
      signingInToken: 'Username + password → nag-verify ang server at nagbabalik ng access + refresh tokens.',
      signingInAutoRefresh: 'Maikli ang buhay ng access tokens; awtomatiko naming ina-refresh nang tahimik gamit ang refresh token.',
      signingInRoles: 'Mga Role: admin at staff. Napipili ang org context sa sidebar.',
      signingInSession: 'Tagal ng Session: Mananatili kayong naka-sign in hanggang 1 araw ng kawalan ng aktibidad. Gumagamit ang app ng maikling 15‑minute access tokens at ina-renew ang mga ito sa background. Pagkatapos ng 1 araw na hindi paggamit ng app (o kung na-clear ang cookies), hihilingin kayong mag-sign in ulit.',
      scootersTitle: 'Mga Scooter — pagdaragdag at pag-edit',
      scootersOpen: 'Buksan ang "Add Scooter" para magrehistro ng bagong sasakyan. Panatilihing maikli at tumpak ang mga detalye.',
      scootersRequired: 'Kailangan: brand/model, status.',
      scootersOptional: 'Opsyonal: purchase cost, fuel/battery details, notes.',
      scootersTip: 'Tip: Inaalis ng status na "maintenance" ang scooter sa availability hanggang maibalik.',
      clientsTitle: 'Mga Client — pagdaragdag at pag-edit',
      clientsOpen: 'Buksan ang "New Client". Kunin ang makakatulong sa inyo na mahanap sila mamaya.',
      clientsRequired: 'Kailangan: buong pangalan.',
      clientsOptional: 'Opsyonal: telepono, email, ID/passport, notes.',
      contractsTitle: 'Mga Contract — paggawa at pamamahala',
      contractsWaysTitle: 'Mga paraan para gumawa ng contract (4 na opsyon)',
      contractsFromContracts: 'Mula sa Contracts: pindutin ang "Add Contract".',
      contractsFromScooters: 'Mula sa Scooters: buksan ang isang scooter → "New Contract".',
      contractsFromClients: 'Mula sa Clients: buksan ang isang client → "New Contract".',
      contractsDuplicate: 'Duplicate: buksan ang isang existing contract → "Duplicate" para ligtas na muling gamitin ang mga detalye.',
      contractsRequiredTitle: 'Mga kailangang field',
      contractsRequiredClient: 'Client at Scooter.',
      contractsRequiredDate: 'Start date/time.',
      contractsRequiredRate: 'Daily rate. (Opsyonal ang Deposit/payment method.)',
      contractsStatusTitle: 'Mga Status at workflow (ano ang pinapayagan)',
      contractsStatusFlow: 'Draft → Active → Closed → Archived.',
      contractsStatusActivate: 'Maaari lang kayong mag-activate kung naka-set na ang mga kailangang field at available ang scooter.',
      contractsStatusClosed: 'Mula sa Closed maaari lang kayong pumunta sa Archived (hindi pwedeng bumalik sa Active).',
      contractsStatusForbidden: 'Bawal: overlapping Active contracts para sa parehong scooter.',
      contractsStatusArchived: 'Nawawala ang Archived contracts sa default views. I-toggle ang "Include archived" para makita ang mga ito.',
      contractsAutoTitle: 'Awtomatikong pag-update ng scooter status',
      contractsAutoCreate: 'Hindi binabago ng paggawa ng contract ang scooter status (anuman ang contract status).',
      contractsAutoActive: 'Kapag naging Active ang isang contract → awtomatikong nagiging "rented" ang scooter.',
      contractsAutoClosed: 'Kapag na-Close/Archive ang isang Active contract → awtomatikong nagiging "available" ang scooter.',
      contractsAutoDraft: 'Kapag ibinalik sa Draft ang isang Active contract → awtomatikong nagiging "available" ang scooter.',
      contractsAutoPrevent: 'Pinipigilan nito ang double-bookings at pinapanatiling tumpak ang availability ng scooter.',
      contractsTimeTitle: 'Pagsubaybay ng oras para sa Active contracts',
      contractsTimeRemaining: 'Ipinapakita ng Active contracts ang natitirang oras hanggang sa end date (hal., "2h 30m left").',
      contractsTimeOverdue: 'Ipinapakita ng Overdue contracts ang lumipas na oras (hal., "Overdue 1d 5h").',
      contractsTimeColors: 'Ipinapahiwatig ng mga kulay ang urgency: Green (4+ hours), Yellow (1-4 hours), Orange (<1 hour), Red (overdue).',
      contractsTimeKanban: 'Sa Kanban view: timer lang ang ipinapakita para sa Active contracts.',
      contractsTimeList: 'Sa List view: parehong status badge at timer ang ipinapakita para sa Active contracts.',
      contractsViewsTitle: 'Mga Contract — views',
      contractsViewsTable: 'Table view: mabilis na pag-scan, pag-filter, at pag-export ng mga row.',
      contractsViewsKanban: 'Kanban board: visual workflow ayon sa status (Draft, Active, Closed, Archived).',
      contractsViewsToggle: 'Gamitin ang view toggle sa Contracts toolbar para magpalit ng views.',
      contractsKanbanTitle: 'Pagtatrabaho sa Kanban board',
      contractsKanbanDrag: 'I-drag & drop ang isang card para ilipat ito sa susunod na pinapayagang status.',
      contractsKanbanEdit: 'Buksan ang isang card at pindutin ang Edit para baguhin ang mga detalye o status nang direkta.',
      contractsKanbanRules: 'Naa-apply ang mga patakaran: hindi kayo pwedeng lumipat mula sa Closed pabalik sa Active; final na ang Archived.',
      contractsKanbanBlocked: 'Naka-block ang mga unavailable moves (hal., naka-Active na ang scooter sa ibang lugar).',
      contractsKanbanFilters: 'Gamitin ang mga filter para mag-focus sa isang client o isama ang Archived items kung kailangan.',
      findingTitle: 'Mabilis na paghahanap (filter & search)',
      findingScooters: 'Mga Scooter: i-filter ayon sa status, brand, model. Gamitin ang search box para sa mabilis na text matches.',
      findingClients: 'Mga Client/User: maghanap ayon sa name/email; ayusin ang page size para makita ang mas maraming row.',
      findingContracts: 'Mga Contract: i-filter ayon sa status; opsyonal na isama ang archived.',
      rowsTitle: 'Ilang row ang ipinapakita namin?',
      rowsScooters: 'Mga Scooter: naglo-load sa mga page at kumukuha ng mas marami kung kailangan (hanggang 100 bawat load).',
      rowsClients: 'Mga Client/User: maaari kayong pumili ng 10/20/30/40/50 row bawat page.',
      rowsContracts: 'Mga Contract: ipinapakita ang current org contracts; isama ang archived para palawakin ang list.',
      themesTitle: 'Mga Theme at defaults',
      themesChange: 'Palitan ang theme (Dark/Light/Blue/Wood) sa kaliwang sidebar sa ibaba.',
      themesAdmin: 'Maaaring i-configure ng mga Admin ang organization defaults sa Management (hal., currency, rates).',
      usersTitle: 'Mga User & Management (admins)',
      usersStaff: 'Staff: pang-araw-araw na operations — tingnan at gumawa ng contracts, pamahalaan ang scooters/clients.',
      usersAdmin: 'Admin: lahat ng kayang gawin ng staff, plus org‑level settings at deletions.',
      tipsTitle: 'Mga Tip & troubleshooting',
      tipsActivate: 'Hindi ma-activate? I-check ang scooter status (dapat available) at mga kailangang field.',
      tipsContract: 'Hindi nakikita ang contract? Maaaring naka-archive — i-enable ang "Include archived".',
      tipsSession: 'Natapos ang session? Kung lagpas na ng isang araw, mag-sign in lang ulit.'
    },
    toolbar: {
      searchPlaceholder: 'Maghanap sa ID, model, o status…',
      clear: 'I-clear',
      listView: 'Listahan',
      kanbanView: 'Kanban',
      calendarView: 'Kalendaryo',
      gridView: 'Grid',
      mapView: 'Mapa',
      filter: 'I-filter',
      export: 'I-export',
      total: 'Kabuuan: {{count}}',
      queryLabel: 'query',
      clearQuery: 'I-clear ang query',
      removeFilter: 'Tanggalin ang {{filter}}'
    },
    ui: {
      zoom: 'Zoom'
    },
    calendar: {
      week: 'Linggo',
      month: 'Buwan',
      all: 'Lahat',
      plusMore: '+{{count}} pa',
      scrollHint: 'Shift+Scroll para mag-move nang pahalang',
      hideEmpty: 'Itago ang scooters na walang upcoming contracts'
    },
    status: {
      available: 'Magagamit',
      rented: 'Nakarenta',
      maintenance: 'Pag-aayos',
      retired: 'Retirado',
      draft: 'Borador',
      active: 'Aktibo',
      closed: 'Sarado',
      archived: 'Naka-archive'
    },
    clients: {
      id: 'Client ID',
      name: 'Pangalan',
      messenger: 'WhatsApp',
      newClient: 'Bagong client',
      creating: 'Gumagawa ng client...',
      fullName: 'Buong Pangalan',
      fullNamePlaceholder: 'Ilagay ang full name',
      email: 'Email',
      emailPlaceholder: 'Ilagay ang email address',
      localPhone: 'Numero ng Telepono',
      localPhonePlaceholder: 'Ilagay ang local phone number (optional)',
      messengerNumber: 'Numero ng WhatsApp',
      countryCodePlaceholder: '63',
      messengerNumberPlaceholder: 'Ilagay ang WhatsApp number (optional)',
      lineId: 'LINE ID',
      lineIdPlaceholder: 'Ilagay ang LINE ID (optional)',
      whatsappButton: 'WhatsApp',
      lineButton: 'LINE',
      contactViaWhatsApp: 'Makipag-ugnayan sa pamamagitan ng WhatsApp',
      contactViaLINE: 'Makipag-ugnayan sa pamamagitan ng LINE',
      notes: 'Mga Tala',
      notesPlaceholder: 'Karagdagang notes tungkol sa client na ito (hal., ibang country phone number)',
      addClient: 'Bagong client',
      detailsTitle: 'Client Details',
      created: 'Ginawa',
      clientId: 'Client ID',
      edit: 'I-edit ang Client',
      previousContracts: 'Nakaraang contracts:',
      noCurrentContracts: 'Walang current contracts',
      editClient: 'I-edit ang Client',
      newContractBtn: 'Bagong Contract',
      close: 'Isara',
      statusHistory: 'Status history',
      unknownClient: 'Hindi kilalang Client',
      fullNameRequired: 'Kailangan ang buong pangalan',
      emailInvalid: 'Hindi wasto ang email address',
      failedLoad: 'Nabigo ang pag-load ng clients',
      noClientsYet: 'Wala pang clients',
      noClientsMatch: 'Walang clients na tumugma sa inyong search',
      addFirstClient: 'Magdagdag ng inyong unang client para makapagsimula',
      tryAdjusting: 'Subukan ang pag-adjust ng inyong search'
    },
    scooters: {
      id: 'Scooter ID',
      modelRequired: 'Kailangan ang model',
      batteryRange: 'Ang battery ay dapat nasa 0–100',
      odometerNegative: 'Hindi pwedeng negative ang odometer',
      fuelRange: 'Ang fuel % ay dapat nasa 0–100',
      incompatibleEnergy: 'Ang electric ay walang fuel; ang fuel vehicles ay walang battery',
      editScooter: 'I-edit ang Scooter',
      newScooter: 'Bagong scooter',
      updating: 'Nag-u-update ng scooter...',
      creating: 'Gumagawa ng scooter...',
      model: 'Modelo',
      modelPlaceholder: 'hal. Ninebot Max',
      status: 'Status',
      typeLevel: 'Uri at Antas',
      odometer: 'Odometer',
      qrCode: 'QR Code',
      qrPlaceholder: 'Optional QR code',
      plateNumber: 'Numero ng Plaka',
      platePlaceholder: 'Optional plate number',
      nickname: 'Palayaw',
      nicknamePlaceholder: 'hal. Red Honda #1',
      nicknameOptional: 'Palayaw (optional)',
      brand: 'Tatak',
      brandPlaceholder: 'Optional brand',
      vehicleType: 'Uri ng Sasakyan',
      electric: 'Elektriko',
      fuel: 'Gasolina',
      batteryPct: 'Baterya %',
      fuelPct: 'Fuel %',
      odometerKm: 'Odometer (km)',
      updateScooter: 'I-update ang scooter',
      addScooter: 'Bagong scooter',
      details: 'Details',
      edit: 'I-edit',
      created: 'Ginawa',
      statusHistory: 'Scooter status history',
      columnScooterId: 'Scooter ID',
      columnBrand: 'Brand',
      columnModel: 'Model',
      columnPlateNumber: 'Numero ng Plaka',
      columnNickname: 'Palayaw',
      columnStatus: 'Status',
      columnVehicleStatus: 'Uri at Antas',
      columnOdometer: 'Odometer',
      pictures: 'Mga Larawan',
      noPhotos: 'Wala pang photos',
      noPhotosYet: 'Wala pang photos',
      allPhotos: 'Lahat ng Photos',
      viewAllPhotos: 'Tingnan lahat ng Photos',
      damage: 'Damage',
      damageOnly: 'Damage',
      showingDamage: 'Nagpapakita ng Damage',
      noDamagePhotos: 'Walang photos na may damage',
      markAsDamage: 'Mark bilang damage',
      markDamage: 'Mark Damage',
      removeDamage: 'Tanggalin ang Damage',
      markNoDamage: 'Mark na walang damage',
      damageDetected: 'Nakita ang damage',
      scooterPhoto: 'Scooter photo',
      selectImageFile: 'Pumili ng image file',
      fileSizeLimit: 'Ang file size ay dapat mas mababa sa 10MB',
      photoUploaded: 'Matagumpay na na-upload ang photo',
      photoDeleted: 'Natanggal ang photo',
      photoDeleteFailed: 'Nabigo ang pagtanggal ng photo',
      photoUpdateFailed: 'Nabigo ang pag-update ng photo',
      photoUploadFailed: 'Nabigo ang pag-upload ng photo',
      photoStorageNotConfigured: 'Hindi pa naka-configure ang photo storage. Pakikontak ang inyong administrator.',
      deletePhoto: 'Tanggalin ang photo',
      drawCircle: 'Gumuhit ng Circle',
      stopDrawing: 'Itigil ang Drawing',
      undo: 'I-undo',
      redo: 'I-redo',
      capture: 'Kunin ang litrato',
      takePhoto: 'Kumuha ng Litrato',
      chooseFromGallery: 'Pumili mula sa Gallery',
      addPhoto: 'Magdagdag ng Litrato',
      photoPreview: 'Preview ng Litrato',
      cameraError: 'Nabigo ang pag-access sa camera',
      cameraPermissionDenied: 'Tinanggihan ang pahintulot sa camera'
    },
    pages: {
      manageColumns: 'Pamahalaan ang columns',
      columns: 'Columns',
      retiredHidden: 'Nakatago ang retired scooters',
      failedLoadScooters: 'Nabigo ang pag-load ng scooters',
      checkConnection: 'Pakitingnan ang inyong connection at subukan ulit.',
      retry: 'Subukan ulit',
      clearFilters: 'I-clear ang filters',
      addScooter: 'Bagong scooter',
      noScootersYet: 'Wala pang scooters',
      noScootersMatch: 'Walang scooters na tumugma sa inyong filters',
      addFirstScooter: 'Magdagdag ng inyong unang scooter para makapagsimula.',
      tryAdjusting: 'Subukan ang pag-adjust o pag-clear ng inyong filters.',
      rowsPerPage: 'Mga row bawat pahina:',
      pageOf: 'Pahina {{current}} ng {{total}}',
      prevShort: 'Nakaraan',
      nextShort: 'Susunod',
      prev: 'Nakaraang page',
      next: 'Susunod na page'
    },
    contractsPage: {
      newContract: 'Bagong Contract',
      showArchived: 'Ipakita ang archived contracts',
      newContractSubtitle: 'Mabilis na gumawa at mag-review ng bagong rental agreement',
      createdDate: 'Petsa ng Paggawa',
      recentlyUpdated: 'Kamakailang Na-update',
      recentlyClosed: 'Kamakailang Na-close',
      allClosed: 'Lahat ng Closed',
      showAll: 'Ipakita lahat',
      showRecent: 'Ipakita ang kamakailan',
      sortBy: 'I-sort ayon sa...',
      archivedOnlyToDraft: 'Ang archived contracts ay maaari lang ilipat sa Draft.'
    },
    activity: {
      title: 'Tala ng Aktibidad',
      subtitle: 'Subaybayan ang aktibidad ng empleyado at mga pagbabago sa system. Tingnan ang user-friendly logs para sa troubleshooting at auditing.',
      filterActivity: 'I-filter ang Activity',
      timePeriod: 'Panahon',
      lastHour: 'Nakaraang Oras',
      today: 'Ngayon',
      thisWeek: 'Sa linggong ito',
      thisMonth: 'Sa buwang ito',
      custom: 'Custom',
      from: 'Mula',
      to: 'Hanggang',
      employee: 'Empleyado',
      type: 'Uri ng Aktibidad',
      allEmployees: 'Lahat ng empleyado',
      allActivities: 'Lahat ng aktibidad',
      scooterMgmt: 'Pamamahala ng Scooter',
      contractMgmt: 'Pamamahala ng Contract',
      clientMgmt: 'Pamamahala ng Client',
      orgSettings: 'Mga Setting ng Organisasyon',
      searchEverything: 'Maghanap ng Lahat',
      searchPlaceholder: 'Maghanap ng names, IDs, prices, statuses...',
      clearAll: 'I-clear lahat'
    },
    contracts: {
      filters: 'Filters',
      editContract: 'I-edit ang Contract',
      dates: 'Dates',
      for: 'para sa',
      scooter: 'Scooter',
      start: 'Start',
      end: 'End',
      duration: 'Duration',
      days: 'days',
      weeks: 'weeks',
      months: 'months',
      estimatedTotal: 'Tinatayang total',
      fetchingData: 'Kumukuha ng clients at scooters',
      scooterCurrentlyRented: 'ℹ️ Ang scooter na ito ay kasalukuyang nakarenta{{until}}.',
      until: 'hanggang',
      futureReservationNote: 'Pwede ka pa ring gumawa ng contract para sa future reservation - siguraduhin lang na hindi mag-o-overlap ang dates.',
      searchClients: 'Maghanap ng clients sa name/email/id',
      noMatches: 'Walang tumugma',
      selected: 'Napili:',
      scooterQuery: 'Scooter (ID o model contains)',
      scooterPlaceholder: 'hal. Ninebot o UUID',
      dailyRateRange: 'Daily rate (min/max)',
      min: 'Min',
      max: 'Max',
      minExceedsMax: 'Hindi pwedeng mas mataas ang Min kaysa sa Max',
      endAfterStart: 'Ang end time ay dapat pagkatapos ng start time',
      clientRequired: 'Kailangan ang client',
      scooterRequired: 'Kailangan ang scooter',
      startTimeRequired: 'Kailangan ang start time',
      dailyRatePositive: 'Ang daily rate ay dapat > 0',
      addContract: 'Bagong contract',
      client: 'Client',
      selectClient: 'Pumili ng client',
      searchScooters: 'Maghanap ng scooters...',
      startTime: 'Start time',
      status: 'Status',
      dailyRate: 'Daily rate',
      deposit: 'Deposit',
      endTime: 'End Date',
      contractId: 'Contract ID',
      paymentMethod: 'Paraan ng Pagbabayad',
      cash: 'Cash',
      local_bank_transfer: 'Local Bank Transfer',
      international_bank_transfer: 'International Bank Transfer',
      notes: 'Mga Tala',
      notesPlaceholder: 'Optional notes tungkol sa contract na ito',
      createDraftForScooter: 'Gumawa ng bagong contract draft para sa scooter na ito.',
      saveAsDraftHint: 'I-save bilang draft para sa pag-activate mamaya',
      startImmediatelyHint: 'Simulan agad ang contract',
      creating: 'Gumagawa...',
      createDraft: 'Gumawa ng draft',
      createActive: 'Gumawa ng active',
      day: 'day',
      forDays: 'para sa {{count}} day',
      forDays_plural: 'para sa {{count}} days',
      total: 'Kabuuan:',
      dayOf: 'day {{day}} ng {{total}}',
      failedLoad: 'Nabigo ang pag-load ng contracts'
    },
    contractsUi: {
      renting: 'nakarenta',
      statusHistory: 'Kasaysayan ng Status',
      noHistory: 'Walang available na history',
      created: 'Ginawa',
      overdue: 'Lumampas na',
      overdueTitle: 'Lumampas na ang contract',
      remainingTitle: 'Natitirang oras hanggang sa pagtatapos ng contract',
      remaining: 'natitira',
      unitDayShort: 'd',
      unitHourShort: 'h',
      unitMinuteShort: 'm',
      clientDetails: 'Mga Detalye ng Client',
      scooterDetails: 'Mga Detalye ng Scooter',
      clientNotFound: 'Hindi nahanap ang client',
      scooterNotFound: 'Hindi nahanap ang scooter',
      fullName: 'Buong Pangalan',
      localPhone: 'Numero ng Telepono',
      messenger: 'WhatsApp',
      brand: 'Brand',
      vehicleType: 'Uri ng Sasakyan',
      battery: 'Baterya',
      qrCode: 'QR Code',
      scooterStatus: 'Status ng Scooter',
      noStatusChanges: 'Wala pang pagbabago ng status',
      loadingHistory: 'Naglo-load ng history...',
      characters: 'mga karakter',
      back: 'Bumalik',
      startDate: 'Petsa ng Simula',
      endDate: 'Petsa ng Pagtatapos',
      contractId: 'Contract ID',
      editContract: 'I-edit ang Contract',
      duplicate: 'I-duplicate',
      duplicateContract: 'I-duplicate ang contract',
      duplicateSubtitle: 'Pumili ng start, duration, at end para sa bagong draft',
      endTimeMustBeAfter: 'Ang end time ay dapat pagkatapos ng start time',
      changeStatus: 'Palitan ang status',
      currentStatus: 'kasalukuyan',
      changeScooterStatus: 'Palitan ang status ng scooter',
      currentScooterStatus: 'kasalukuyan',
      showingRange: 'Nagpapakita ng {{start}}–{{end}} ng {{total}}',
      contractCreatedTitle: 'Nagawa ang contract',
      contractCreatedMessage: 'Gusto mo bang buksan ang contract na ginawa?',
      contractLabel: 'Contract',
      openContract: 'Buksan ang Contract',
      stayHere: 'Manatili dito',
      scooterStatusQuestion: 'Ano ang dapat na status ng scooter "{{model}}"?',
      currentStatusLabel: 'Kasalukuyang status:',
      statusChangeHelp: 'Makakatulong ito sa inyo na pamahalaan ang availability ng scooter pagkatapos ng contract status change.',
      working: 'Nagtatrabaho…',
      moveContractAndUpdate: 'Ilipat ang Contract & I-update ang Scooter',
      enterToConfirm: 'Enter para i-confirm · Esc para i-cancel',
      statusAvailable: 'Magagamit',
      statusAvailableDesc: 'Handa na ang scooter para sa rental',
      statusRented: 'Nakarenta',
      statusRentedDesc: 'Kasalukuyang nakarenta ang scooter',
      statusMaintenance: 'Pag-aayos',
      statusMaintenanceDesc: 'Kailangan ng maintenance ang scooter',
      statusRetired: 'Retirado',
      statusRetiredDesc: 'Hindi na sa serbisyo ang scooter',
      statusCurrent: 'Kasalukuyan',
      moveContractTitle: 'Ilipat ang Contract',
      moveContractMessage: 'Pakipili ang bagong status para sa scooter kapag inililipat ang contract na ito.',
      fromLabel: 'Mula',
      toLabel: 'Patungo',
      clientLabel: 'Client',
      scooterLabel: 'Scooter',
      currentContract: 'Kasalukuyang contract:',
      viewContract: 'Tingnan ang Contract'
    },
    contractDocument: {
      print: 'I-print',
      saveAsPDF: 'I-save bilang PDF',
      email: 'Email',
      addSignature: 'Magdagdag ng Signature',
      emailRequired: 'Kailangan ang email address',
      emailSent: 'Matagumpay na naipadala ang contract',
      enterSignatureName: 'Ilagay ang signer name para idagdag sa document',
      loading: 'Naglo-load ng contract...',
      preparingPDF: 'Inihahanda ang PDF...',
      downloadSuccess: 'Matagumpay na na-download ang PDF',
      downloadFailed: 'Bigo sa pag-generate ng PDF',
      retry: 'Subukan ulit',
      emailContract: 'I-email ang Contract',
      recipientEmail: 'Email ng Tatanggap',
      sending: 'Nagpapadala...',
      send: 'Ipadala'
    },
    businessDetails: {
      title: 'Business Details',
      settingsUpdated: 'Matagumpay na na-update ang business details',
      errorLoading: 'Error sa pag-load ng business details.',
      helpText: 'Punan ang inyong business information sa ibaba. Ang mga details na ito ay lalabas sa rental contracts. Iwanan ang fields na blank kung mas gusto ninyong punan ito nang kamay sa printed contracts.',
      businessName: 'Business Name',
      businessNamePlaceholder: 'Ilagay ang inyong business name',
      businessAddress: 'Business Address',
      businessAddressPlaceholder: 'Ilagay ang inyong business address',
      businessPhone: 'Business Phone',
      businessPhonePlaceholder: '+63 2 123 4567',
      businessEmail: 'Business Email',
      businessEmailPlaceholder: 'hal. business@example.com',
      taxId: 'Tax ID / Rehistro ng Negosyo',
      taxIdPlaceholder: '1234567890123',
      representativeName: 'Pangalan ng Kinatawan',
      representativeNamePlaceholder: 'Juan dela Cruz',
      representativeIdNumber: 'ID Number ng Kinatawan',
      representativeIdNumberPlaceholder: '1234567890123',
      saving: 'Nagse-save...',
      saveDetails: 'I-save ang Business Details',
      saved: 'Na-save na'
    },
    messaging: {
      title: 'Makipag-ugnayan sa Customer',
      scanQR: 'I-scan ang QR Code',
      orClickLink: 'O i-click ang link sa ibaba',
      openApp: 'Buksan ang Messaging App',
      selectTemplate: 'Pumili ng Message Template',
      customMessage: 'Pasadyang Mensahe',
      thankYou: 'Salamat',
      reminder: 'Paalala',
      payment: 'Paalala sa Pagbabayad',
      templateThankYou: 'Salamat sa pagpili sa {{businessName}}! Umaasa kami na nasiyahan kayo sa inyong scooter rental experience. Umaasa kaming makapagserbisyo ulit sa inyo!',
      templateReminder: 'Hi {{clientName}}! Ito ay {{businessName}}. Reminder: Matatapos bukas ang inyong rental period. Pakibalik ang scooter.',
      templatePayment: 'Hi {{clientName}}! Ito ay {{businessName}}. Payment reminder para sa contract #{{contractId}}.',
      noWhatsApp: 'Walang available na WhatsApp number',
      noLINE: 'Walang available na LINE ID',
      close: 'Isara',
      messagePreview: 'Preview ng Mensahe'
    },
    finance: {
      title: 'Pangkalahatang Pananalapi',
      exportCSV: 'I-export ang CSV',
      timeRange: 'Saklaw ng Panahon',
      day: 'Araw',
      week: 'Linggo',
      month: 'Buwan',
      year: 'Taon',
      all: 'Lahat',
      searchPlaceholder: 'Maghanap ng scooters...',
      showSoldOnly: 'Ipakita lang ang nabenta',
      totalPurchase: 'Kabuuang Pagbili',
      totalMaintenance: 'Kabuuang Pagpapanatili',
      totalIncome: 'Kabuuang Kita',
      netProfit: 'Netong Kita',
      topROIScooter: 'Top ROI Scooter',
      bestBrandModel: 'Pinakamahusay na Brand/Model',
      scooters: 'scooters',
      scooter: 'Scooter',
      purchase: 'Pagbili',
      maintenance: 'Pagpapanatili',
      income: 'Kita',
      sale: 'Pagbebenta',
      net: 'Neto',
      roi: 'ROI',
      status: 'Status',
      profitable: 'Kumikita',
      breakingEven: 'Break Even',
      lossMaker: 'Nalulugi',
      totals: 'Kabuuan',
      recentExpenses: 'Kamakailang Gastos',
      noExpenses: 'Walang na-record na expenses',
      loadingExpenses: 'Naglo-load ng expenses...',
      noExpensesInRange: 'Walang expenses sa napiling time range',
      expenseTotal: 'Kabuuan',
      categoryOilChange: 'Pagpapalit ng Langis',
      categoryTires: 'Mga Gulong',
      categoryBrakes: 'Mga Preno',
      categoryEngine: 'Makina',
      categoryBattery: 'Baterya',
      categoryBodyParts: 'Mga Bahagi ng Katawan',
      categoryCleaning: 'Paglinis',
      categoryRegistration: 'Pagrehistro',
      categoryInsurance: 'Seguro',
      categoryMiscParts: 'Iba pang Bahagi',
      categoryCustom: 'Custom',
      affectsExpenseDetails: 'nakakaapekto sa expense details',
      tableView: 'Talahanayan',
      chartsView: 'Mga Tsart',
      toggleKPIs: 'I-toggle ang KPIs',
      toggleInsights: 'I-toggle ang Insights',
      toggleAdditionalTables: 'I-toggle ang Additional Tables',
      showOperational: 'Ipakita ang operational view',
      showFull: 'Ipakita ang full view',
      operationalView: 'Operational view',
      fullView: 'Full view',
      operationalNet: 'Operational net',
      date: 'Petsa',
      category: 'Kategorya',
      amount: 'Halaga',
      note: 'Mga Tala',
      actions: 'Mga Aksyon',
      edit: 'I-edit',
      delete: 'Tanggalin',
      editExpense: 'I-edit ang Expense',
      customLabel: 'Custom label',
      confirmDeleteExpense: 'Sigurado ka bang gusto mong tanggalin ang expense na ito?',
      expenseDeleted: 'Natanggal ang expense',
      deleteFailed: 'Nabigo ang pagtanggal ng expense',
      expenseUpdated: 'Na-update ang expense',
      updateFailed: 'Nabigo ang pag-update ng expense',
      expenseCreated: 'Nagawa ang expense',
      addExpense: 'Magdagdag ng Expense',
      brandModelPerformance: 'Brand/Model Performance',
      brand: 'Brand',
      model: 'Model',
      count: 'Count',
      avgROI: 'Avg ROI',
      topExpenses: 'Top Expenses ayon sa Category',
      noData: 'Walang data',
      monthlyTrends: 'Monthly Financial Trends',
      expenseBreakdown: 'Expense Category Breakdown',
      maintenanceEfficiency: 'Maintenance Efficiency',
      topPerformers: 'Top Performers',
      needsAttention: 'Kailangan ng Atensyon',
      fleetHealth: 'Fleet Health Overview',
      totalScooters: 'Kabuuang Scooters',
      revenueVsMaintenance: 'Revenue vs Maintenance ayon sa Scooter',
      profitTrend: 'Profit Trend ayon sa Scooter',
      statusDistribution: 'Status Distribution',
      exportBasic: 'I-export ang Basic Summary',
      exportDetailed: 'I-export ang Detailed Expenses',
      exportingDetailed: 'Nag-e-export ng detailed expenses...',
      exportCompleted: 'Natapos ang export',
      exportFailed: 'Nabigo ang export'
    },
    landing: {
      heroTitle: '2026 na. Gumagamit ka pa rin ng spreadsheets.',
      heroSubtitle: 'Mag-digitalize ka na.',
      heroDescription: 'Ang scooter rental platform na talagang gumagana.',
      heroDescription2: 'Tigilan ang pagkalugi. Magsimula nang may saysay.',
      ctaButton: 'Tingnan ang Pagganap →',
      ctaLink: 'O patuloy na gawin sa papel, nasa sa iyo',
      contractTimeLabel: 'Average na oras ng paggawa ng kontrata',
      contractTimeHint: 'Ganun kabilis ka makakagawa ng kontrata. Walang paghihintay. Walang lag.',
      problemsTitle: 'Mali ka siguro.',
      problemsSubtitle: '(Sinabi namin ang sinabi namin.)',
      problem1Title: 'Ang spreadsheets ay hindi business model',
      problem1Desc: 'Kung mag-crash ang Excel, mag-crash din ang negosyo mo. Hindi yan resilience, yan ay Russian roulette sa kita mo. Gumising ka - hindi na 2015.',
      problem2Title: 'Nawawalan ka ng 15-30% sa manual errors',
      problem2Desc: 'Double-booked na scooters. Nakalimutang renewals. Nawalang kontrata. Bawat manual na hakbang ay pera na umaalis. Tigilan mo na.',
      problem3Title: 'Ang \'sistema\' mo ay WhatsApp + memory',
      problem3Desc: 'Mga mensahe ng client na nakakalat sa 3 apps. Mga petsa ng kontrata sa utak mo. Pag-track ng bayad sa notebook. Pare, hindi na 2010. Sumabay ka na.',
      problem4Title: 'Hindi mo ma-scale ang hindi mo ma-measure',
      problem4Desc: 'Aling scooter ang pinaka-profitable? Aling client ang pinakamatagal mag-renta? Ano ang actual ROI mo? Kung hindi mo alam, bulag ka. At mahal yan.',
      challengeTitle: 'Sa tingin mo mali kami?',
      challengeText: 'Patunayan mo. Ipakita mo ang sistema mong papel at ipapakita namin sa iyo kung ano ang hitsura ng 2026.',
      challengeButton: 'Ipakita Mo sa Akin ang Hitsura ng 2026 →',
      featuresTitle: 'Ang makukuha mo (na hindi mo alam na kailangan mo)',
      featuresSubtitle: 'Tunay na functionality. Tunay na bilis. Tunay na resulta.',
      featuresSubtitle2: 'Walang palabok, puro katotohanan lang.',
      feature1Title: 'Real-time fleet dashboard',
      feature1Desc: 'Tingnan lahat. Alamin lahat. Gumawa ng aksyon sa lahat. Available, rented, maintenance - lahat sa isang tingin. Wala nang "check ko lang ang notes ko" moments.',
      feature1Item1: 'Live status updates sa buong fleet mo',
      feature1Item2: 'Revenue tracking na may saysay talaga',
      feature1Item3: 'ROI calculations na hindi kailangan ng PhD',
      feature2Title: 'Mga kontrata na hindi basura',
      feature2Desc: 'Gumawa ng kontrata sa ilang segundo. I-track sila nang real-time. Hindi na mag-double-book. Hindi na mawalan ng renewal date. Hindi na magtaka "sino may scooter #42?"',
      feature2Item1: 'Kanban view dahil hindi visual ang spreadsheets',
      feature2Item2: 'Automatic conflict detection (oo, chine-check namin)',
      feature2Item3: 'Delivery planning na talagang gumagana',
      feature3Title: 'Renewals & reminders',
      feature3Desc: 'Insurance renewal reminders. Registration. Coverage. Contract end dates. Lahat tracked. Lahat automated. Lahat nagre-remind sa iyo bago mahuli ang lahat. Dahil expired insurance = mahal na problema. At missed contract renewals = nawalang kita.',
      feature3Item1: 'Insurance renewal reminders (huwag mong hayaang ma-expire)',
      feature3Item2: 'Contract due reminders (hindi na makakalimot ng renewal)',
      feature3Item3: 'Status badges: Active, Due Soon, Overdue (malalaman mo)',
      feature4Title: 'Calendar view na may saysay',
      feature4Desc: 'Tingnan ang buong schedule ng fleet mo sa isang tingin. Alamin kung kailan naka-renta ang mga scooter. Magplano nang maaga. Wala nang double-booking. Wala nang "kailan ba matatapos ang kontratang ito?" confusion.',
      feature4Item1: 'Visual timeline ng lahat ng kontrata',
      feature4Item2: 'Tingnan ang availability sa isang tingin',
      feature4Item3: 'Magplano ng deliveries at pickups nang propesyonal',
      feature5Title: 'QR code = instant customer contact',
      feature5Desc: 'I-scan ang QR code. I-message ang customer mo agad. Wala nang paghahanap sa WhatsApp. Wala nang "sino na naman ito?" moments. I-scan lang, i-message, tapos. 2026 na, kumilos ka na.',
      feature5Item1: 'QR codes sa bawat scooter',
      feature5Item2: 'Isang scan = WhatsApp/Line na may pre-filled message',
      feature5Item3: 'Wala nang paghahanap ng customer contacts',
      qrPhoneLabel: 'Phone Number',
      qrPhoneHint: 'Maglagay ng anumang numero para sa testing (hal., numero ng boyfriend ng asawa mo)',
      qrMessageLabel: 'Message',
      qrMessageHint: 'Maaari mong baguhin ang mensaheng ito para i-customize ang makikita ng customers',
      qrScanHint: 'I-scan gamit ang phone mo para i-test',
      qrClickLink: 'O i-click para buksan ang WhatsApp',
      feature6Title: 'Unlimited users. Full transparency.',
      feature6Desc: 'Magdagdag ng kasing dami ng team members na kailangan mo. Walang per-user fees. Nakikita ng admins ang lahat - sino ang gumawa ng ano, kailan, at bakit. Buong activity log. Walang lihim. Walang sorpresa.',
      feature6Item1: 'Unlimited users (seryoso, walang limit)',
      feature6Item2: 'Kumpletong activity log para sa admins',
      feature6Item3: 'Role-based permissions (user/admin)',
      feature7Title: 'Insurance reminders na nagse-save ng pera mo',
      feature7Desc: 'Huwag nang hayaang ma-expire ang insurance. Makakuha ng reminders bago mahuli ang lahat. Custom renewal windows. Smart alerts. Dahil expired insurance = mahal na problema. At hindi namin gusto ang mahal na problema.',
      feature7Item1: 'Insurance renewal reminders (hindi makakalimot ng deadline)',
      feature7Item2: 'Custom reminder windows (7 araw, 30 araw, ikaw ang pumili)',
      feature7Item3: 'Dashboard alerts na talagang nakaka-attract ng atensyon',
      feature8Title: 'Maintenance tracking & profitability analytics',
      feature8Desc: 'I-track ang bawat expense. Alamin kung aling scooter ang nagpapakita sa iyo. Tingnan ang ROI per scooter. I-track ang maintenance costs. Gumawa ng data-driven decisions. Tigilan ang paghula.',
      feature8Item1: 'Buong maintenance expense tracking',
      feature8Item2: 'Per-scooter profitability analytics',
      feature8Item3: 'ROI calculations na talagang mahalaga',
      feature9Title: 'Photos & mobile view na talagang gumagana',
      feature9Desc: 'Kumuha ng photos on the go. Mobile-optimized interface. Camera integration. Photo galleries per scooter. Markahan ang damage direkta sa photos. I-filter ayon sa damage status. Dahil ang isang larawan ay katumbas ng libong salita. At libong salita ay hindi makakatulong sa iyo sa korte.',
      feature9Item1: 'Mobile view na optimized para sa field work',
      feature9Item2: 'Camera integration - kumuha ng photos direkta sa app',
      feature9Item3: 'Markahan ang damage sa photos gamit ang visual indicators',
      feature9Item4: 'I-filter ang photos ayon sa damage status',
      feature10Title: 'Propesyonal na contract generation',
      feature10Desc: 'Auto-fill ang karamihan ng fields. Preview bago mag-print. Thai at English versions. Print-ready format. Wala nang manual typing. Wala nang errors. Gumawa lang, preview, print. Tapos.',
      feature10Item1: 'Auto-fill mula sa client & scooter data',
      feature10Item2: 'Preview bago mag-print (walang sorpresa)',
      feature10Item3: 'Kasama ang Thai & English versions',
      speedTitle: 'Napakabilis, iisipin mong sira',
      speedDesc: 'React Query caching. Prefetch-on-hover. Token rehydration. Code splitting. Lazy loading. Optimistic updates.',
      speedDesc2: 'Nagsasalita kami ng wika mo: bilis.',
      speedMetric1: 'Page Load Time',
      speedMetric2: 'Search & Filter',
      speedMetric3: 'Data Refresh',
      finalCTATitle: 'Handa na bang tigilan ang pagkalugi?',
      finalCTAText: 'O patuloy na gawin sa papel. Hindi kami ang boss mo.',
      finalCTAText2: '(Pero mas maganda sana ang kita mo. Sinasabi lang.)',
      finalCTAButton: 'Tingnan ang Pagganap →',
      finalCTALink: 'O sabihin na mali kami',
      viewFullContract: 'Tingnan ang Buong Kontrata'
    }
  }
} as const;

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: savedLang,
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
    defaultNS: 'common',
    ns: ['common', 'sidebar', 'toolbar', 'status', 'clients', 'scooters', 'contracts', 'contractsUi', 'pages', 'contractsPage', 'activity', 'home', 'users', 'management', 'login', 'toasts', 'businessDetails', 'messaging', 'finance', 'landing'],
    returnNull: false,
  });

// Expose i18next on window for components that access via window.i18next
if (typeof window !== 'undefined') {
  (window as any).i18next = i18n;
}

// Expose a helper to switch language and persist
export function setLanguage(lang: 'en' | 'th' | 'fil') {
  i18n.changeLanguage(lang);
  try { localStorage.setItem(STORAGE_KEY, lang); } catch {}
}

export default i18n;


